import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { SubTabs, ObsFeedback, Select } from '../ui';
import { useSidePanel } from '../../context/SidePanelContext';
import { useUser } from '../../context/UserContext';
import { deriveConditions, strategyHasRules } from '../../data/strategyConditions';
import { getTabReports, generateTabReport } from '../../api/ai';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// All journal endpoints now require auth — include cookie on every request.
function apiFetch(path, opts = {}) {
  return fetch(`${API}${path}`, { credentials: 'include', ...opts });
}

/* ── Condition check icon ── */
function CondIcon({ st }) {
  const s = st==='ok'
    ? {bg:'rgba(26,106,26,.15)',c:'var(--up-field-ink)',ch:'✓'}
    : st==='warn'
    ? {bg:'rgba(138,90,0,.15)',c:'var(--amber-ink)',ch:'!'}
    : {bg:'rgba(136,30,16,.15)',c:'var(--down-field-ink)',ch:'✕'};
  return (
    <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',
      width:16,height:16,borderRadius:'50%',background:s.bg,color:s.c,
      fontWeight:700,fontSize:11,flexShrink:0}}>{s.ch}</span>
  );
}

function ConfirmModal({ title, message, confirmLabel='Delete', onConfirm, onCancel }) {
  if (!onConfirm) return null;
  return (
    <div style={{position:'fixed',inset:0,zIndex:9999,background:'rgba(0,0,0,.45)',
      display:'flex',alignItems:'center',justifyContent:'center'}}
      onMouseDown={onCancel}>
      <div onMouseDown={e=>e.stopPropagation()}
        style={{background:'var(--surface)',border:'.5px solid var(--hair-strong)',borderRadius:6,
          padding:'20px 24px',minWidth:280,maxWidth:360,boxShadow:'0 8px 32px rgba(0,0,0,.25)'}}>
        <div style={{fontSize:12,fontWeight:700,color:'var(--ink)',marginBottom:6}}>{title||'Confirm'}</div>
        <div style={{fontSize:11,color:'var(--ink-2)',marginBottom:18,lineHeight:1.6}}>{message}</div>
        <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
          <button onClick={onCancel}
            style={{fontSize:11,fontWeight:600,padding:'4px 14px',borderRadius:4,
              border:'.5px solid var(--hair-strong)',background:'transparent',
              color:'var(--ink-2)',cursor:'pointer'}}>Cancel</button>
          <button onClick={onConfirm}
            style={{fontSize:11,fontWeight:600,padding:'4px 14px',borderRadius:4,
              border:'none',background:'var(--down-field-ink)',color:'#fff',cursor:'pointer'}}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const RefreshIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"
    style={{display:'inline-block',verticalAlign:'middle',marginRight:3}}>
    <path d="M21 12a9 9 0 1 1-2.64-6.36"/>
    <path d="M21 4v4.5h-4.5"/>
  </svg>
);

/* ── Status badge ── */
function StatusBadge({ conds, isManual }) {
  if (isManual) return <span className="pos-badge-manual">Manual</span>;
  const broken = conds.filter(c=>c.st==='fail').length;
  const warned  = conds.filter(c=>c.st==='warn').length;
  if (broken>0) return <span className="pos-badge-alert"  style={{display:'inline-flex',alignItems:'center',gap:4}}><span style={{width:8,height:8,borderRadius:'50%',background:'#b71c1c',display:'inline-block'}}/> Alert</span>;
  if (warned>0) return <span className="pos-badge-watch"  style={{display:'inline-flex',alignItems:'center',gap:4}}><span style={{fontSize:11}}>⚠</span> Watch</span>;
  return         <span className="pos-badge-holding" style={{display:'inline-flex',alignItems:'center',gap:4}}><span style={{width:8,height:8,borderRadius:'50%',background:'#2e7d32',display:'inline-block'}}/> Holding</span>;
}

/* ── Status filter pill group — matching screenshot ── */
function StatusFilter({ value, onChange }) {
  return (
    <div style={{display:'inline-flex',borderRadius:5,border:'.5px solid var(--hair-strong)',overflow:'hidden'}}>
      {['All','Open','Closed'].map((s,i) => (
        <button key={s} onClick={()=>onChange(s)}
          style={{
            fontSize:11, padding:'3px 12px', cursor:'pointer', border:'none',
            borderLeft: i>0 ? '.5px solid var(--hair-strong)' : 'none',
            background: value===s ? 'var(--amber-ink)' : 'var(--surface)',
            color:      value===s ? '#fff'           : 'var(--ink-2)',
            fontWeight: value===s ? 600 : 400,
            transition:'background .15s',
          }}>{s}</button>
      ))}
    </div>
  );
}

/* ── Open Positions ── */
function OpenPositionsTab({ goToJournal }) {
  const { open: openPanel } = useSidePanel();
  const [positions, setPositions]       = useState([]);
  const [livePrices, setLivePrices]     = useState({});   // { symbol: ltp }
  const [conditions, setConditions]     = useState({});   // { symbol: conditionData }
  const [loading, setLoading]           = useState(true);
  const [priceErr, setPriceErr]         = useState(false);
  const [refreshKey, setRefreshKey]     = useState(0);

  // ── Fetch open trades + group by groupId ──────────────────────────────────
  useEffect(() => {
    setLoading(true);
    apiFetch(`/api/journal/trades?status=Open`)
      .then(r => r.json())
      .then(data => {
        const trades = data.trades || [];
        const groups = {};
        trades.forEach(t => {
          const gid = t.groupId || String(t.utn);
          if (!groups[gid]) {
            groups[gid] = {
              groupId:    gid,
              underlying: t.underlying,
              strategy:   t.strategy || 'Manual',
              dateOpen:   t.dateOpen,
              legs:       [],
            };
          }
          groups[gid].legs.push(t);
          if (!groups[gid].dateOpen || t.dateOpen < groups[gid].dateOpen)
            groups[gid].dateOpen = t.dateOpen;
        });
        setPositions(Object.values(groups));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [refreshKey]);

  // ── Fetch spot LTP + condition data for each unique underlying ───────────
  useEffect(() => {
    if (!positions.length) return;
    const syms = [...new Set(positions.map(p => p.underlying))];
    setPriceErr(false);

    // Prices
    Promise.allSettled(
      syms.map(sym =>
        fetch(`${API}/api/prices/stock/${encodeURIComponent(sym)}`)
          .then(r => r.ok ? r.json() : null)
      )
    ).then(results => {
      const prices = {};
      let anyErr = false;
      results.forEach((r, i) => {
        if (r.status === 'fulfilled' && r.value?.ltp != null) {
          prices[syms[i]] = r.value.ltp;
        } else { anyErr = true; }
      });
      setLivePrices(prices);
      if (anyErr && !Object.keys(prices).length) setPriceErr(true);
    });

    // Conditions — one request per unique (underlying, direction)
    // direction: if any leg is Buy CE/FUT → bull, if Buy PE → bear
    const condRequests = {};
    positions.forEach(pos => {
      const dir = pos.legs.some(l => l.type === 'PE') ? 'short' : 'long';
      const key = `${pos.underlying}::${dir}`;
      if (!condRequests[key]) condRequests[key] = { sym: pos.underlying, dir };
    });

    Promise.allSettled(
      Object.values(condRequests).map(({ sym, dir }) =>
        fetch(`${API}/api/scanner/conditions/${encodeURIComponent(sym)}?direction=${dir}`)
          .then(r => r.ok ? r.json() : { source: 'error', error: `HTTP ${r.status}` })
          .catch(e => ({ source: 'error', error: e.message }))
      )
    ).then(results => {
      const conds = {};
      Object.values(condRequests).forEach(({ sym, dir }, i) => {
        const r = results[i];
        // Always write — fulfilled or rejected — so UI never stays in "Fetching…"
        conds[`${sym}::${dir}`] = r.status === 'fulfilled'
          ? (r.value ?? { source: 'error', error: 'empty response' })
          : { source: 'error', error: r.reason?.message };
      });
      setConditions(conds);
    });
  }, [positions]);

  // ── Live P&L for a group (best-effort using spot LTP) ────────────────────
  const computeGroupPnl = useCallback((group) => {
    const ltp = livePrices[group.underlying];
    if (ltp == null) return null;
    let pnl = 0;
    group.legs.forEach(leg => {
      const entry   = leg.entry   || 0;
      const lots    = leg.lots    || 1;
      const lotSize = leg.lotSize || 1;
      const dir     = leg.bs === 'Sell' ? -1 : 1;
      pnl += (ltp - entry) * lots * lotSize * dir;
    });
    return Math.round(pnl);
  }, [livePrices]);

  if (loading) {
    return (
      <div style={{padding:'24px',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
        ⏳ Loading open positions…
      </div>
    );
  }

  const totalLegs = positions.reduce((s, g) => s + g.legs.length, 0);

  return (
    <div>
      {/* Header */}
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
        <div>
          <div style={{fontSize:12,fontWeight:700,color:'var(--ink)'}}>Open Positions Monitor</div>
          <div style={{fontSize:11,color:'var(--ink-3)'}}>
            {positions.length} group{positions.length!==1?'s':''} · {totalLegs} leg{totalLegs!==1?'s':''}
            {priceErr && <span style={{color:'var(--amber-ink)',marginLeft:6}}>· Kite not connected — live P&L unavailable</span>}
          </div>
        </div>
        <div style={{display:'flex',gap:6}}>
          <button onClick={()=>setRefreshKey(k=>k+1)}
            style={{padding:'5px 10px',borderRadius:4,fontSize:11,border:'.5px solid var(--hair-strong)',background:'var(--surface)',color:'var(--ink-2)',cursor:'pointer'}}>
            <RefreshIcon/> Refresh
          </button>
          <button onClick={goToJournal}
            style={{padding:'5px 12px',borderRadius:4,fontSize:11,fontWeight:700,background:'var(--amber-ink)',color:'#fff',border:'none',cursor:'pointer'}}>
            + Log new trade
          </button>
        </div>
      </div>

      {/* Empty state */}
      {positions.length === 0 && (
        <div className="card" style={{padding:'32px',textAlign:'center'}}>
          <div style={{fontSize:14,color:'var(--ink-3)',marginBottom:8}}>No open positions</div>
          <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:14}}>Log a trade without an exit date/price to see it here.</div>
          <button onClick={goToJournal}
            style={{padding:'6px 16px',borderRadius:4,fontSize:11,fontWeight:700,background:'var(--amber-ink)',color:'#fff',border:'none',cursor:'pointer'}}>
            + Log new trade
          </button>
        </div>
      )}

      {/* Position cards */}
      <div style={{display:'flex',flexDirection:'column',gap:8}}>
        {[...positions].sort((a, b) => {
          const priority = (pos) => {
            if (pos.strategy === 'Manual') return 3;
            const dir = pos.legs.some(l => l.type === 'PE') ? 'short' : 'long';
            const cd = conditions[`${pos.underlying}::${dir}`];
            const cds = deriveConditions(pos.strategy, cd) || [];
            if (cds.some(c => c.st === 'fail')) return 0;
            if (cds.some(c => c.st === 'warn')) return 1;
            return 2;
          };
          return priority(a) - priority(b);
        }).map(pos => {
          const isGroup  = pos.legs.length > 1;
          const isManual = pos.strategy === 'Manual';
          const ltp      = livePrices[pos.underlying];
          const livePnl  = computeGroupPnl(pos);
          const pnlPos   = livePnl != null ? livePnl >= 0 : true;
          const firstLeg = pos.legs[0];

          // Direction for condition lookup
          const dir          = pos.legs.some(l => l.type === 'PE') ? 'short' : 'long';
          const condKey      = `${pos.underlying}::${dir}`;
          const condData     = conditions[condKey];
          const hasRules     = strategyHasRules(pos.strategy);
          const conds        = !isManual ? deriveConditions(pos.strategy, condData) : null;

          // StatusBadge based on conditions
          const hasFail = conds?.some(c => c.st === 'fail');
          const hasWarn = conds?.some(c => c.st === 'warn');

          // Entry cost summary
          const totalCost = pos.legs.reduce((s, leg) =>
            s + (leg.entry || 0) * (leg.lots || 1) * (leg.lotSize || 1), 0);
          const pnlPct = totalCost > 0 && livePnl != null
            ? ((livePnl / totalCost) * 100).toFixed(1) : null;

          return (
            <div key={pos.groupId} className="card" style={{overflow:'hidden'}}>

              {/* Row 1: status badge + ticker + meta */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'8px 12px',gap:8,flexWrap:'wrap'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,flex:1,minWidth:0}}>
                  <StatusBadge conds={conds || []} isManual={isManual}/>
                  <span style={{fontWeight:700,fontSize:13,color:'var(--amber-ink)',cursor:'pointer',
                    borderBottom:'1px dotted var(--amber-ink)',whiteSpace:'nowrap'}}
                    onClick={()=>openPanel({name:pos.underlying,sector:''})}>
                    {pos.underlying}
                  </span>
                  {!isGroup && firstLeg && (
                    <span style={{fontSize:11,color:'var(--ink-3)'}}>
                      {firstLeg.inst || firstLeg.type}
                      {' · '}{firstLeg.bs} {firstLeg.lots} lot{firstLeg.lots!==1?'s':''}
                      {' · Entry ₹'}{firstLeg.entry}
                    </span>
                  )}
                  {isGroup && (
                    <span style={{fontSize:11,color:'var(--ink-3)'}}>
                      {pos.legs.length} legs · {pos.legs.map(l=>l.type).join('/')}
                    </span>
                  )}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0}}>
                  {ltp != null && (
                    <span style={{fontSize:11,fontFamily:'var(--font-data)',
                      color:'var(--ink-2)',padding:'2px 7px',borderRadius:3,
                      background:'var(--surface-2)',border:'.5px solid var(--hair-strong)'}}>
                      LTP ₹{ltp.toLocaleString()}
                    </span>
                  )}
                  <span style={{fontSize:11,color:'var(--ink-3)'}}>
                    {pos.strategy}{isGroup?' · Group':''} · Opened {pos.dateOpen}
                  </span>
                </div>
              </div>

              {/* Row 2: conditions LEFT + P&L RIGHT */}
              <div style={{display:'flex',alignItems:'stretch',justifyContent:'space-between',
                borderTop:'.5px solid var(--hair)',gap:0}}>

                {/* Conditions / legs (left) */}
                <div style={{flex:1,minWidth:0,padding:'8px 12px'}}>
                  {isManual ? (
                    <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic'}}>
                      Manual trade — no strategy conditions tracked.
                      {firstLeg?.notes && <span style={{color:'var(--ink-2)',marginLeft:6,fontStyle:'normal'}}>Note: {firstLeg.notes}</span>}
                    </div>
                  ) : conds ? (
                    <div style={{display:'flex',flexDirection:'column',gap:5}}>
                      {/* Group legs summary if multi-leg */}
                      {isGroup && (
                        <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:4}}>
                          {pos.legs.map((leg, li) => (
                            <span key={li} style={{fontSize:11,padding:'1px 7px',borderRadius:3,
                              background:leg.bs==='Buy'?'rgba(26,106,26,.1)':'rgba(136,30,16,.1)',
                              color:leg.bs==='Buy'?'var(--up-field-ink)':'var(--down-field-ink)',
                              border:`.5px solid ${leg.bs==='Buy'?'rgba(26,106,26,.2)':'rgba(136,30,16,.2)'}`}}>
                              {leg.bs} {leg.inst||leg.type} · {leg.lots}L @ ₹{leg.entry}
                            </span>
                          ))}
                        </div>
                      )}
                      {/* Condition rows */}
                      {conds.map((c, ci) => (
                        <div key={ci} style={{display:'flex',alignItems:'center',gap:7,
                          fontSize:11,color:'var(--ink-2)'}}>
                          <CondIcon st={c.st}/>
                          <span>
                            {c.st==='fail' && <strong style={{color:'var(--down-field-ink)'}}>Broken: </strong>}
                            {c.st==='warn' && <strong style={{color:'var(--amber-ink)'}}>Watch: </strong>}
                            {c.label}
                          </span>
                        </div>
                      ))}
                      {/* Cached data notice */}
                      {condData?.source === 'cached' && (
                        <div style={{fontSize:11,color:'var(--ink-3)',marginTop:2,fontStyle:'italic'}}>
                          ⚠ Conditions from cached data ({condData.evaluated_at}) — Kite offline
                        </div>
                      )}
                      {condData?.source === 'none' && (
                        <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic'}}>
                          No condition data available — connect Kite to check live
                        </div>
                      )}
                    </div>
                  ) : !hasRules ? (
                    /* Strategy has no scanner rules — position is genuinely unmonitored */
                    <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11,color:'var(--amber-ink)'}}>
                      <span style={{fontWeight:700}}>!</span>
                      <span>No monitoring rules defined for "{pos.strategy}" — position is unmonitored (Watch).</span>
                    </div>
                  ) : condData === undefined ? (
                    /* Still fetching */
                    <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic'}}>
                      Fetching conditions…
                    </div>
                  ) : condData?.source === 'error' ? (
                    /* Fetch failed — backend may still be deploying */
                    <div style={{fontSize:11,color:'var(--amber-ink)',fontStyle:'italic'}}>
                      ⚠ Could not load conditions for {pos.underlying} — backend may still be starting up. Refresh in a moment.
                    </div>
                  ) : (
                    <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic'}}>
                      No condition data available — connect Kite to check live.
                    </div>
                  )}
                </div>

                {/* P&L (right) */}
                <div style={{flexShrink:0,textAlign:'right',padding:'8px 14px',
                  borderLeft:'.5px solid var(--hair)',minWidth:110,display:'flex',
                  flexDirection:'column',justifyContent:'center',alignItems:'flex-end'}}>
                  {livePnl != null ? (
                    <>
                      <div style={{fontWeight:700,fontSize:15,
                        color:pnlPos?'var(--up-field-ink)':'var(--down-field-ink)',
                        fontFamily:'var(--font-data)',whiteSpace:'nowrap'}}>
                        {pnlPos?'+':'−'}₹{Math.abs(livePnl).toLocaleString()}
                      </div>
                      {pnlPct != null && (
                        <div style={{fontSize:11,color:pnlPos?'var(--up-field-ink)':'var(--down-field-ink)',
                          fontFamily:'var(--font-data)'}}>
                          {pnlPos?'+':'−'}{Math.abs(parseFloat(pnlPct)).toFixed(1)}%
                        </div>
                      )}
                      <div style={{fontSize:11,color:'var(--ink-3)',marginTop:2}}>Live (spot)</div>
                    </>
                  ) : (
                    <div style={{fontSize:11,color:'var(--ink-3)',fontStyle:'italic'}}>
                      {priceErr ? 'Kite offline' : 'Fetching…'}
                    </div>
                  )}
                </div>
              </div>

              {/* Row 3: footer */}
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                padding:'4px 12px',background:'var(--surface-2)',gap:8}}>
                <div style={{fontSize:11,color:'var(--ink-3)'}}>
                  Cost basis: <strong>₹{totalCost.toLocaleString(undefined,{maximumFractionDigits:0})}</strong>
                  {isGroup && ` · ${pos.legs.length} legs`}
                  {condData?.stars && (
                    <span style={{marginLeft:8,color:'var(--amber-ink)'}}>
                      {'★'.repeat(condData.stars)} confidence
                    </span>
                  )}
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <div style={{fontSize:11,color:'var(--ink-3)',fontFamily:'var(--font-data)'}}>
                    GID: {pos.groupId}
                  </div>
                  <button onClick={goToJournal}
                    style={{fontSize:11,padding:'1px 8px',borderRadius:3,
                      border:'.5px solid var(--hair-strong)',background:'var(--surface)',
                      color:'var(--ink-3)',cursor:'pointer'}}>
                    Edit in Journal
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ── Trade Journal ── */
const ACCOUNTS_BASE = ['Zerodha','Upstox','Kotak','ICICI','Definedge'];
const SEGMENTS      = ['F&O','Equity','Commodity','Currency'];

// Strategy dropdown: two-section grouping ("From Strategies" / "Manual")
// Populated from /api/strategies registry; rendered as grouped <select>
function StrategySelect({ value, onChange, registryStrategies, placeholder }) {
  const knownValues = ['', 'Manual', 'Import', ...registryStrategies.map(s => s.name)];
  const isCustom = value && !knownValues.includes(value);
  const [customVal, setCustomVal] = useState(isCustom ? value : '');

  return (
    <>
      <Select value={isCustom ? '__custom__' : (value || '')}
        onChange={e => {
          if (e.target.value === '__custom__') {
            onChange(customVal || '');
          } else {
            setCustomVal('');
            onChange(e.target.value);
          }
        }}>
        {placeholder
        ? <option value="">{placeholder}</option>
        : <option value="Manual">Manual / Ad-hoc</option>
      }
        <optgroup label="Manual">
          {placeholder && <option value="Manual">Manual / Ad-hoc</option>}
          <option value="Import">Imported</option>
        </optgroup>
        {registryStrategies.length > 0 && (
          <optgroup label="From Strategies">
            {registryStrategies.map(s => (
              <option key={s.id} value={s.name}>{s.name}</option>
            ))}
          </optgroup>
        )}
        <option value="__custom__">+ New custom strategy…</option>
      </Select>
      {(isCustom || value === '__custom__') && (
        <input className="inp" style={{marginTop:3}}
          value={customVal}
          placeholder="Enter strategy name"
          onChange={e => { setCustomVal(e.target.value); onChange(e.target.value); }}/>
      )}
    </>
  );
}

function BulkAccountSelect({ value, accounts, onChange }) {
  const isNew = value && !accounts.includes(value);
  return (
    <>
      <Select value={isNew ? '__new__' : value}
        onChange={e => onChange(e.target.value === '__new__' ? '' : e.target.value)}>
        <option value="">Account (keep existing)</option>
        {accounts.map(a => <option key={a} value={a}>{a}</option>)}
        <option value="__new__">+ New account name…</option>
      </Select>
      {isNew && (
        <input className="inp" style={{marginTop:3}} value={value}
          placeholder="Enter account name"
          onChange={e=>onChange(e.target.value)}/>
      )}
    </>
  );
}

// Flat list of all strategy names (for filter dropdown)
function strategyFilterOptions(registryStrategies, journalStrategies) {
  const fromReg = registryStrategies.map(s => s.name);
  const extra   = journalStrategies.filter(n => !fromReg.includes(n) && n !== 'Manual' && n !== 'Import');
  return ['Manual', 'Import', ...fromReg, ...extra];
}

const EMPTY = {
  groupId:'', underlying:'', segment:'F&O', strategy:'Manual',
  inst:'', bs:'Buy', type:'CE', lots:1, lotSize:25, strike:'', expiry:'',
  acct:'Zerodha', entry:'', exit:'', dateOpen:'', dateClose:'', notes:'', status:'Open',
};

// Date formatter — DD-MMM-YY (for display in table)
const fmtD = iso => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'2-digit'}).replace(/ /g,'-');
  } catch { return iso; }
};

// Expiry formatter — YYYY-MM-DD → DDMmmYY (e.g. 2025-01-25 → 25Jan25)
const fmtExpiry = iso => {
  if (!iso) return '';
  try {
    const [y, m, d] = iso.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d}${months[+m - 1]}${y.slice(2)}`;
  } catch { return iso; }
};

// !! CRITICAL: F must be defined OUTSIDE TradeJournalTab.
// If defined inside, React creates a new component type on every render,
// unmounting and remounting inputs — destroying focus on every keystroke.
function F({ label, children }) {
  return (
    <div style={{display:'flex',flexDirection:'column',gap:2}}>
      <div style={{fontSize:11,color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'.04em'}}>{label}</div>
      {children}
    </div>
  );
}

// ── Underlying autocomplete ───────────────────────────────────────────────────
function UnderlyingAutocomplete({ value, onChange, onBlur, universe }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);

  const matches = React.useMemo(() => {
    if (!value || value.length < 1) return [];
    const q = value.toUpperCase();
    return universe.filter(s => s.includes(q)).slice(0, 8);
  }, [value, universe]);

  React.useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} style={{position:'relative'}}>
      <input className="inp"
        value={value}
        onChange={e => { onChange(e.target.value.toUpperCase()); setOpen(true); }}
        onFocus={() => value && setOpen(true)}
        onBlur={e => { setTimeout(() => setOpen(false), 150); onBlur && onBlur(e); }}
        onKeyDown={e => {
          if (e.key === 'Enter' && matches.length > 0) {
            onChange(matches[0]); setOpen(false);
          }
          if (e.key === 'Escape') setOpen(false);
        }}
        autoComplete="off"
      />
      {open && matches.length > 0 && (
        <div style={{
          position:'absolute', top:'100%', left:0, zIndex:200,
          background:'var(--surface)', border:'.5px solid var(--hair)',
          borderRadius:6, boxShadow:'0 4px 16px rgba(0,0,0,.15)',
          minWidth:160, maxHeight:220, overflowY:'auto', marginTop:2,
        }}>
          {matches.map(sym => (
            <div key={sym}
              onMouseDown={e => { e.preventDefault(); onChange(sym); setOpen(false); }}
              style={{padding:'5px 10px', cursor:'pointer', fontSize:11,
                fontWeight:600, color:'var(--ink)',
                borderBottom:'.5px solid var(--hair)',
                fontFamily:'var(--font-ui)'}}
              onMouseEnter={e => e.currentTarget.style.background='var(--surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              {sym}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ── Notes tooltip cell ── */
function NotesCell({ notes }) {
  const [show, setShow] = React.useState(false);
  if (!notes) return <td style={{ width: 28 }} />;
  return (
    <td style={{ width: 28, textAlign: 'center', position: 'relative' }}>
      <span
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 16, height: 16, borderRadius: '50%', background: 'rgba(138,90,0,.15)',
          color: 'var(--amber-ink)', fontSize: 10, fontWeight: 700, cursor: 'default',
          userSelect: 'none', lineHeight: 1 }}>
        i
      </span>
      {show && (
        <div style={{ position: 'absolute', bottom: 'calc(100% + 4px)', left: '50%',
          transform: 'translateX(-50%)', zIndex: 200,
          background: 'var(--surface)', border: '.5px solid var(--hair-strong)',
          borderRadius: 4, padding: '6px 8px', fontSize: 11, color: 'var(--ink)',
          maxWidth: 240, minWidth: 120, lineHeight: 1.5,
          boxShadow: '0 4px 12px rgba(0,0,0,.18)',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word', pointerEvents: 'none',
          textAlign: 'left' }}>
          {notes}
        </div>
      )}
    </td>
  );
}

function TradeJournalTab({ initialFilters, subtabH = 40 }) {
  const filtersRef = React.useRef(null);
  const [filtersH, setFiltersH] = React.useState(44);
  const { open: openPanel } = useSidePanel();
  const { user, isAdmin } = useUser();
  const isSuperAdmin = user?.role === 'super_admin';
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [view,    setView]    = useState('trade');
  const FILTER_DEFAULTS = {status:'All',segment:'All',strategy:'All',underlying:'',acct:'All',from:'',to:''};
  const [filters, setFilters] = useState({...FILTER_DEFAULTS,...(initialFilters||{})});
  const [drilldownLabel, setDrilldownLabel] = useState(() => {
    if (!initialFilters) return null;
    const parts = [];
    if (initialFilters.acct) parts.push(`Account: ${initialFilters.acct}`);
    if (initialFilters.strategy) parts.push(`Strategy: ${initialFilters.strategy}`);
    if (initialFilters.underlying) parts.push(`Underlying: ${initialFilters.underlying}`);
    if (initialFilters.status && initialFilters.status !== 'All') parts.push(`Status: ${initialFilters.status}`);
    return parts.length ? parts.join(' · ') : null;
  });
  const [sort,    setSort]    = useState({col:'dateOpen', dir:-1});
  const [showNew,    setShowNew]    = useState(false);
  const [editUTN,    setEditUTN]    = useState(null);
  const [newT,    setNewT]    = useState({...EMPTY, dateOpen:new Date().toISOString().split('T')[0]});
  const [expandedGroups, setExpandedGroups] = useState({});
  const [expiries,       setExpiries]       = useState([]);
  const [expiriesLoading, setExpiriesLoading] = useState(false);
  const [customExpiry, setCustomExpiry] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEdits, setBulkEdits]     = useState({groupId:'', strategy:'', acct:''});
  const [confirmModal, setConfirmModal] = useState(null); // {title, message, onConfirm}
  const [bulkLoading, setBulkLoading]  = useState(false);

  // ── Strategy registry (for dropdown grouping) ────────────────────────────
  const [registryStrategies, setRegistryStrategies] = useState([]);
  useEffect(() => {
    apiFetch('/api/strategies')
      .then(r => r.json())
      .then(d => setRegistryStrategies(d.strategies ?? []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const el = filtersRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setFiltersH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Broker import state ──────────────────────────────────────────────────
  const [importState,    setImportState]    = useState('idle'); // idle|loading|review|committing
  const [importPreview,  setImportPreview]  = useState(null);
  const [importApproved, setImportApproved] = useState({});    // { index: bool }
  const [importError,    setImportError]    = useState(null);
  const fileInputRef = React.useRef(null);

  const handleImportFileSelected = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';  // reset so same file can be re-selected
    if (!file) return;
    setImportState('loading');
    setImportError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await apiFetch('/api/journal/import/preview', { method: 'POST', body: fd });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Upload failed (${res.status})`);
      }
      const data = await res.json();
      setImportPreview(data);
      const approved = {};
      data.positions.forEach((p, i) => { approved[i] = !p.is_duplicate; });
      setImportApproved(approved);
      setImportState('review');
    } catch(e) {
      setImportError(e.message);
      setImportState('idle');
    }
  };

  const handleImportCommit = async () => {
    setImportState('committing');
    const positions = (importPreview?.positions || []).filter((_, i) => importApproved[i]);
    try {
      const res = await apiFetch('/api/journal/import/commit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ positions }),
      });
      if (!res.ok) throw new Error(`Commit failed (${res.status})`);
      const result = await res.json();
      setImportState('idle');
      setImportPreview(null);
      await loadTrades();
      if (result.errors?.length) {
        setError(`Imported ${result.imported} trades. ${result.errors.length} failed.`);
      }
    } catch(e) {
      setImportError(e.message);
      setImportState('review');
    }
  };

  // ── Super-admin: view another user's trades ──────────────────────────────
  const [viewAsUser,  setViewAsUser]  = useState(null);   // { google_id, name, email } | null
  const [userList,    setUserList]    = useState([]);      // users who have trades
  useEffect(() => {
    if (!isSuperAdmin) return;
    apiFetch('/api/journal/users-with-trades')
      .then(r => r.json())
      .then(d => setUserList(d.users ?? []))
      .catch(() => {});
  }, [isSuperAdmin]);

  // ── Load trades from API ─────────────────────────────────────────────────
  const loadTrades = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const qs  = viewAsUser ? `?as_user=${encodeURIComponent(viewAsUser.google_id)}` : '';
      const res = await apiFetch(`/api/journal/trades${qs}`);
      const data = await res.json();
      setLogs(data.trades ?? []);
    } catch(e) {
      setError('Could not load trades. Is the backend running?');
    } finally {
      setLoading(false);
    }
  }, [viewAsUser]);

  // ── Load symbol universe for underlying autocomplete ─────────────────────
  const [symbolUniverse, setSymbolUniverse] = useState([]);
  useEffect(() => {
    apiFetch(`/api/journal/symbol-universe`)
      .then(r => r.json())
      .then(d => setSymbolUniverse(d.symbols ?? []))
      .catch(() => {});
  }, []);

  // Re-add newly saved symbols to universe so they appear immediately
  const addToUniverse = useCallback((sym) => {
    if (sym && !symbolUniverse.includes(sym.toUpperCase())) {
      setSymbolUniverse(u => [...u, sym.toUpperCase()].sort());
    }
  }, [symbolUniverse]);

  useEffect(() => { loadTrades(); }, [loadTrades]);

  // Dynamic strategy list: registry names + any names already in the journal
  const journalStrategyNames = useMemo(() => {
    const fromJournal = logs.map(t => t.strategy).filter(Boolean);
    return [...new Set(fromJournal)];
  }, [logs]);
  const strategies = useMemo(
    () => strategyFilterOptions(registryStrategies, journalStrategyNames),
    [registryStrategies, journalStrategyNames],
  );

  // Dynamic accounts list: base list + any account names already in the journal
  const accounts = useMemo(() => {
    const fromJournal = logs.map(t => t.acct).filter(Boolean);
    return [...new Set([...ACCOUNTS_BASE, ...fromJournal])];
  }, [logs]);

  // ── Live P&L state ──────────────────────────────────────────────────────────
  const [livePnl,        setLivePnl]        = useState({});  // { trade_id: {ltp, live_pnl} }
  const [livePnlLoading, setLivePnlLoading] = useState(false);
  const [livePnlTs,      setLivePnlTs]      = useState(null); // timestamp of last fetch

  const fetchLivePnl = useCallback(async () => {
    setLivePnlLoading(true);
    try {
      const res  = await apiFetch('/api/journal/live-pnl');
      const data = await res.json();
      setLivePnl(data.pnl || {});
      setLivePnlTs(new Date());
    } catch (e) {
      // silent — table just shows stored P&L
    } finally {
      setLivePnlLoading(false);
    }
  }, []);

  // Fetch on load (after trades arrive) and every 15 minutes
  useEffect(() => {
    if (!logs.length) return;
    fetchLivePnl();
    const iv = setInterval(fetchLivePnl, 15 * 60 * 1000);
    return () => clearInterval(iv);
  }, [fetchLivePnl, logs.length]);

  const handleSort = col => setSort(s => s.col===col ? {col,dir:-s.dir} : {col,dir:-1});

  const SortTH = ({col, children, style={}}) => {
    const active = sort.col===col;
    return (
      <th onClick={()=>handleSort(col)} style={{cursor:'pointer',userSelect:'none',...style}}>
        {children}
        <span style={{marginLeft:2,opacity:active?1:.3,fontSize:11,color:active?'var(--amber-ink)':'var(--ink-3)'}}>
          {active?(sort.dir>0?'↑':'↓'):'↕'}
        </span>
      </th>
    );
  };

  // Filter
  const filtered = useMemo(()=>logs.filter(t=>{
    if (filters.status!=='All'   && t.status!==filters.status) return false;
    if (filters.segment!=='All'  && (t.segment||'F&O')!==filters.segment) return false;
    if (filters.strategy!=='All' && t.strategy!==filters.strategy) return false;
    if (filters.underlying && !t.underlying.toLowerCase().includes(filters.underlying.toLowerCase())) return false;
    if (filters.acct!=='All' && t.acct!==filters.acct) return false;
    if (filters.from && t.dateOpen<filters.from) return false;
    if (filters.to   && t.dateOpen>filters.to)   return false;
    return true;
  }),[logs,filters]);

  // Sort helper
  const sortRows = rows => [...rows].sort((a,b)=>{
    const v = col => {
      if (col==='dateOpen'||col==='dateClose') return a[col]||'';
      if (col==='pnl') return a.pnl??0;
      if (col==='underlying'||col==='strategy'||col==='segment'||col==='acct'||col==='status'||col==='bs'||col==='type') return (a[col]||'').toLowerCase();
      return a[col]??'';
    };
    const av = sort.col==='pnl' ? (a.pnl??0) : (a[sort.col]||'');
    const bv = sort.col==='pnl' ? (b.pnl??0) : (b[sort.col]||'');
    if (typeof av==='string') return av.localeCompare(bv)*sort.dir;
    return (av-bv)*sort.dir;
  });

  const sortedTrades = useMemo(()=>sortRows(filtered), [filtered, sort]);

  // Group view: aggregate trades by groupId
  const groupedRows = useMemo(()=>{
    const groups = {};
    filtered.forEach(t=>{
      const gid = t.groupId||t.utn;
      if (!groups[gid]) groups[gid] = {
        groupId:gid, underlying:t.underlying, strategy:t.strategy,
        segment:t.segment||'F&O', acct:t.acct, status:t.status,
        dateOpen:t.dateOpen, dateClose:t.dateClose,
        legs:[], netPnl:0, openLegs:0, closedLegs:0,
      };
      groups[gid].legs.push(t);
      groups[gid].netPnl += (t.pnl||0);
      if (t.status==='Open')   groups[gid].openLegs++;
      else                     groups[gid].closedLegs++;
      // Use earliest open date and latest close date for the group
      if (!groups[gid].dateOpen || t.dateOpen < groups[gid].dateOpen) groups[gid].dateOpen = t.dateOpen;
      if (t.dateClose && (!groups[gid].dateClose || t.dateClose > groups[gid].dateClose)) groups[gid].dateClose = t.dateClose;
      // Group is open if any leg is open
      if (t.status==='Open') groups[gid].status = 'Open';
    });
    const arr = Object.values(groups);
    return [...arr].sort((a,b)=>{
      const av = sort.col==='pnl' ? a.netPnl : (a[sort.col]||'');
      const bv = sort.col==='pnl' ? b.netPnl : (b[sort.col]||'');
      if (typeof av==='string') return av.localeCompare(bv)*sort.dir;
      return (av-bv)*sort.dir;
    });
  },[filtered,sort]);

  const autoGroupId = ()=>`${newT.dateOpen?.replace(/-/g,'')||'YYYYMMDD'}_${newT.underlying||'TICKER'}_${newT.strategy?.replace(/\s+/g,'')||'STRAT'}`;

  const saveTrade = async () => {
    const gid = newT.groupId || autoGroupId();
    const payload = {
      group_id:   gid,
      underlying: newT.underlying,
      segment:    newT.segment,
      strategy:   newT.strategy,
      inst:       computedInst || newT.inst,
      bs:         newT.bs,
      type:       newT.type,
      lots:       +newT.lots,
      lot_size:   +newT.lotSize,
      acct:       newT.acct,
      entry:      newT.entry ? +newT.entry : null,
      exit:       newT.exit  ? +newT.exit  : null,
      date_open:  newT.dateOpen  || null,
      date_close: newT.dateClose || null,
      notes:      newT.notes,
      strike:     newT.strike || null,
      expiry:     newT.expiry || null,
    };
    try {
      if (editUTN !== null) {
        // editUTN is the integer PK used in the API URL
        const res = await apiFetch(`/api/journal/trades/${editUTN}`, {
          method: 'PUT',
          headers: {'Content-Type':'application/json'},
          body: JSON.stringify(payload),
        });
        const updated = await res.json();
        setLogs(l => l.map(t => t.id === editUTN ? updated : t));
      } else {
        const res = await apiFetch(`/api/journal/trades`, {
          method: 'POST',
          headers: {'Content-Type':'application/json'},
          body: JSON.stringify(payload),
        });
        const created = await res.json();
        setLogs(l => [created, ...l]);
      }
      setShowNew(false);
      setEditUTN(null);
      setNewT({...EMPTY, dateOpen:new Date().toISOString().split('T')[0]});
      addToUniverse(newT.underlying);
    } catch(e) {
      setError('Could not save trade.');
    }
  };

  const editTrade = t => {
    // Spread t over EMPTY so all fields (including strike, expiry from DB) carry over
    setNewT({...EMPTY, ...t});
    setEditUTN(t.id);   // integer PK used for PUT /api/journal/trades/{id}
    setShowNew(true);
    window.scrollTo({top:0, behavior:'smooth'});
  };

  const copyTrade = t => {
    const today = new Date().toISOString().split('T')[0];
    // Keep groupId (so copy joins the same group), underlying, strategy, inst,
    // segment, type, bs, lots, lotSize, entry, strike, expiry, acct.
    // Reset: dateOpen → today, dateClose → '', exit → null, notes → '', pnl → 0.
    setNewT({...EMPTY, ...t, dateOpen:today, dateClose:'', exit:'', notes:'', pnl:0});
    setEditUTN(null);   // null = new trade (POST)
    setShowNew(true);
    window.scrollTo({top:0, behavior:'smooth'});
  };

  const cancelForm = () => {
    setShowNew(false);
    setEditUTN(null);
    setNewT({...EMPTY, dateOpen:new Date().toISOString().split('T')[0]});
  };

  const deleteTrade = (id) => {
    setConfirmModal({
      title: 'Delete trade',
      message: 'This will permanently remove this trade from your journal. This cannot be undone.',
      onConfirm: async () => {
        setConfirmModal(null);
        try {
          await apiFetch(`/api/journal/trades/${id}`, { method: 'DELETE' });
          setLogs(l => l.filter(t => t.id !== id));
          setSelectedIds(s => { const n = new Set(s); n.delete(id); return n; });
        } catch(e) { setError('Could not delete trade.'); }
      },
    });
  };

  const applyBulkEdit = async () => {
    if (!selectedIds.size) return;
    setBulkLoading(true);
    const ids = [...selectedIds];
    const updates = {};
    if (bulkEdits.groupId.trim()) updates.group_id = bulkEdits.groupId.trim();
    if (bulkEdits.strategy.trim()) updates.strategy = bulkEdits.strategy.trim();
    if (bulkEdits.acct.trim()) updates.acct = bulkEdits.acct.trim();
    if (!Object.keys(updates).length) { setBulkLoading(false); return; }
    try {
      await Promise.all(ids.map(id => {
        const t = logs.find(x => x.id === id);
        if (!t) return Promise.resolve();
        return apiFetch(`/api/journal/trades/${id}`, {
          method: 'PUT',
          headers: {'Content-Type':'application/json'},
          body: JSON.stringify({
            underlying: t.underlying, segment: t.segment, strategy: updates.strategy ?? t.strategy,
            inst: t.inst, bs: t.bs, type: t.type, lots: t.lots, lot_size: t.lotSize,
            acct: updates.acct ?? t.acct, entry: t.entry, exit: t.exit,
            date_open: t.dateOpen, date_close: t.dateClose, notes: t.notes,
            strike: t.strike, expiry: t.expiry, group_id: updates.group_id ?? t.groupId,
          }),
        });
      }));
      await loadTrades();
      setSelectedIds(new Set());
      setBulkEdits({groupId:'', strategy:'', acct:''});
    } catch(e) { setError('Bulk edit failed.'); }
    setBulkLoading(false);
  };

  const bulkDelete = () => {
    if (!selectedIds.size) return;
    setConfirmModal({
      title: `Delete ${selectedIds.size} trade${selectedIds.size !== 1 ? 's' : ''}`,
      message: `This will permanently remove ${selectedIds.size} trade${selectedIds.size !== 1 ? 's' : ''} from your journal. This cannot be undone.`,
      onConfirm: async () => {
        setConfirmModal(null);
        setBulkLoading(true);
        try {
          await Promise.all([...selectedIds].map(id =>
            apiFetch(`/api/journal/trades/${id}`, { method: 'DELETE' })
          ));
          await loadTrades();
          setSelectedIds(new Set());
        } catch(e) { setError('Bulk delete failed.'); }
        setBulkLoading(false);
      },
    });
  };

  const setField = (k,v) => setNewT(t=>({...t,[k]:v}));

  // ── Auto-computed instrument (read-only in form) ─────────────────────────
  const computedInst = useMemo(() => {
    const { underlying, segment, type, strike, expiry } = newT;
    if (!underlying) return '';
    if (segment === 'Equity' || segment === 'Currency') return underlying;
    const exp = fmtExpiry(expiry);
    if (!exp) return underlying;               // expiry not yet chosen
    if (type === 'FUT') return `${underlying} ${exp} FUT`;
    if (type === 'CE' || type === 'PE') {
      return strike ? `${underlying} ${strike} ${type} ${exp}` : `${underlying} ${type} ${exp}`;
    }
    return underlying;
  }, [newT.underlying, newT.segment, newT.type, newT.strike, newT.expiry]);

  // ── Fetch expiry list when underlying or segment changes ─────────────────
  useEffect(() => {
    if (!newT.underlying || !['F&O','Commodity'].includes(newT.segment)) {
      setExpiries([]);
      return;
    }
    let cancelled = false;
    setExpiriesLoading(true);
    apiFetch(`/api/journal/expiries/${newT.underlying}`)
      .then(r => r.json())
      .then(d => {
        if (!cancelled) {
          const allExpiries = d.expiries ?? [];
          setExpiries(allExpiries);
          setExpiriesLoading(false);
          // Auto-select nearest upcoming if nothing selected
          if (!cancelled && !newT.expiry) {
            const today = new Date().toISOString().split('T')[0];
            const nearest = allExpiries.find(e => e >= today);
            if (nearest) setField('expiry', nearest);
          }
        }
      })
      .catch(() => { if (!cancelled) { setExpiries([]); setExpiriesLoading(false); } });
    return () => { cancelled = true; };
  }, [newT.underlying, newT.segment]);

  // ── Auto-fill lot size when underlying or segment changes ────────────────
  useEffect(() => {
    if (!newT.underlying) return;
    if (!['F&O','Commodity'].includes(newT.segment)) {
      setNewT(t => ({...t, lotSize: 1}));
      return;
    }
    let cancelled = false;
    apiFetch(`/api/journal/lot-size/${encodeURIComponent(newT.underlying)}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!cancelled && d?.lot_size) setNewT(t => ({...t, lotSize: d.lot_size})); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [newT.underlying, newT.segment]);

  return (
    <div>
      {/* Super-admin: view-as-user banner */}
      {isSuperAdmin && userList.length > 0 && (
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,
          padding:'6px 10px',background:'rgba(74,144,217,.07)',
          border:'.5px solid rgba(74,144,217,.25)',borderRadius:6,flexWrap:'wrap'}}>
          <span style={{fontSize:11,fontWeight:600,color:'var(--amber-ink)',letterSpacing:'.05em',textTransform:'uppercase'}}>
            👁 Viewing:
          </span>
          <button
            onClick={() => setViewAsUser(null)}
            style={{fontSize:11,padding:'2px 8px',borderRadius:4,border:'.5px solid var(--hair-strong)',
              background: viewAsUser === null ? 'var(--amber-ink)' : 'var(--surface-2)',
              color: viewAsUser === null ? '#fff' : 'var(--ink-2)',
              cursor:'pointer',fontWeight:600}}>
            My Trades
          </button>
          {userList.map(u => (
            <button key={u.google_id}
              onClick={() => setViewAsUser(u)}
              style={{fontSize:11,padding:'2px 8px',borderRadius:4,border:'.5px solid var(--hair-strong)',
                background: viewAsUser?.google_id === u.google_id ? 'var(--amber-ink)' : 'var(--surface-2)',
                color: viewAsUser?.google_id === u.google_id ? '#fff' : 'var(--ink-2)',
                cursor:'pointer'}}>
              {u.name || u.email || u.google_id}
              <span style={{opacity:.55,marginLeft:4}}>({u.trade_count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Error banner */}
      {error && (
        <div style={{marginBottom:8,padding:'7px 12px',background:'rgba(136,30,16,.08)',
          border:'.5px solid rgba(136,30,16,.3)',borderRadius:6,fontSize:11,color:'var(--down-field-ink)',
          display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          {error}
          <button onClick={()=>setError(null)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--down-field-ink)',fontSize:13}}>✕</button>
        </div>
      )}

      {drilldownLabel && (
        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6,
          padding:'5px 10px',background:'rgba(138,90,0,.08)',
          border:'.5px solid rgba(138,90,0,.3)',borderRadius:20,
          fontSize:11,color:'var(--amber-ink)',width:'fit-content'}}>
          <span style={{fontWeight:600}}>Filtered:</span>
          <span>{drilldownLabel}</span>
          <button onClick={()=>{setFilters({...FILTER_DEFAULTS});setDrilldownLabel(null);}}
            style={{background:'none',border:'none',cursor:'pointer',color:'var(--amber-ink)',
              fontSize:13,lineHeight:1,padding:0,marginLeft:2}}>×</button>
        </div>
      )}

      {/* Controls — sticky below subtab */}
      <div ref={filtersRef} style={{ position: 'sticky', top: TOPBAR_H + subtabH, zIndex: 9,
        background: 'var(--paper)', paddingBottom: 8 }}>
      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
        <div className="seg">
          <button className={`sbtn${view==='trade'?' on':''}`}  onClick={()=>{setView('trade');setSelectedIds(new Set());}}>Trade View</button>
          <button className={`sbtn${view==='group'?' on':''}`}  onClick={()=>{setView('group');setSelectedIds(new Set());}}>Group View</button>
        </div>
        <StatusFilter value={filters.status} onChange={s=>setFilters(f=>({...f,status:s}))}/>
        <Select value={filters.segment} onChange={e=>setFilters(f=>({...f,segment:e.target.value}))}>
          <option value="All">All Segments</option>
          {SEGMENTS.map(s=><option key={s}>{s}</option>)}
        </Select>
        <Select value={filters.strategy} onChange={e=>setFilters(f=>({...f,strategy:e.target.value}))}>
          <option value="All">All Strategies</option>
          {strategies.map(s=><option key={s}>{s}</option>)}
        </Select>
        <input className="inp" placeholder="Underlying..." value={filters.underlying}
          onChange={e=>setFilters(f=>({...f,underlying:e.target.value}))} style={{minWidth:100}}/>
        <Select value={filters.acct} onChange={e=>setFilters(f=>({...f,acct:e.target.value}))}>
          <option value="All">All Accounts</option>
          {accounts.map(a=><option key={a}>{a}</option>)}
        </Select>
        <input className="inp" type="date" value={filters.from} onChange={e=>setFilters(f=>({...f,from:e.target.value}))} style={{width:110}}/>
        <span style={{fontSize:11,color:'var(--ink-3)'}}>to</span>
        <input className="inp" type="date" value={filters.to} onChange={e=>setFilters(f=>({...f,to:e.target.value}))} style={{width:110}}/>
        <button className="pill on" style={{marginLeft:'auto'}} onClick={()=>{
          if (showNew) { cancelForm(); }
          else { setEditUTN(null); setNewT({...EMPTY,dateOpen:new Date().toISOString().split('T')[0]}); setShowNew(true); }
        }}>
          {showNew?'Cancel':'+ New Trade'}
        </button>
        <button className="pill" title="Refresh live P&L" onClick={fetchLivePnl}
          style={{display:'flex',alignItems:'center',gap:4,opacity:livePnlLoading?0.5:1}}>
          {livePnlLoading ? '⏳' : <RefreshIcon/>} Live P&L
          {livePnlTs && <span style={{fontSize:11,color:'var(--ink-3)',marginLeft:2}}>
            {livePnlTs.toLocaleTimeString('en-IN',{hour:'2-digit',minute:'2-digit'})}
          </span>}
        </button>
        <input type="file" ref={fileInputRef} style={{display:'none'}} accept=".xlsx,.csv"
          onChange={handleImportFileSelected}/>
        <button className="pill" onClick={()=>fileInputRef.current?.click()}
          disabled={importState==='loading'||importState==='committing'}
          style={{opacity:(importState==='loading'||importState==='committing')?0.5:1}}>
          {importState==='loading' ? '⏳ Parsing…' : '⬆ Import'}
        </button>
      </div>
      </div>{/* end sticky controls wrapper */}

      {selectedIds.size > 0 && (
        <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',marginBottom:8,
          padding:'8px 10px',background:'var(--surface-2)',borderRadius:6,
          border:'.5px solid var(--hair-strong)'}}>
          <span style={{fontSize:11,fontWeight:700,color:'var(--amber-ink)',flexShrink:0}}>
            {selectedIds.size} selected
          </span>
          <input className="inp" value={bulkEdits.groupId} placeholder="Group ID (leave blank to keep)"
            onChange={e=>setBulkEdits(b=>({...b,groupId:e.target.value}))} style={{minWidth:120}}/>
          <StrategySelect value={bulkEdits.strategy} onChange={v=>setBulkEdits(b=>({...b,strategy:v}))}
            registryStrategies={registryStrategies} placeholder="Strategy (keep existing)"/>
          {/* Account dropdown for bulk edit */}
          <BulkAccountSelect
            value={bulkEdits.acct}
            accounts={accounts}
            onChange={v=>setBulkEdits(b=>({...b,acct:v}))}/>
          <button className="pill on" onClick={applyBulkEdit}
            disabled={bulkLoading||(!bulkEdits.groupId.trim()&&!bulkEdits.strategy.trim()&&!bulkEdits.acct.trim())}
            style={{opacity:bulkLoading?0.5:1}}>
            {bulkLoading ? 'Saving…' : 'Apply'}
          </button>
          <button className="pill" onClick={bulkDelete} disabled={bulkLoading}
            style={{color:'var(--down-field-ink)',borderColor:'var(--down-field-ink)'}}>
            Delete
          </button>
          <button className="pill" onClick={()=>{setSelectedIds(new Set());setBulkEdits({groupId:'',strategy:'',acct:''}); }}
            style={{marginLeft:'auto'}}>
            Clear selection
          </button>
        </div>
      )}

      {/* Import error */}
      {importError && (
        <div style={{marginBottom:8,padding:'7px 12px',background:'rgba(136,30,16,.08)',
          border:'.5px solid rgba(136,30,16,.3)',borderRadius:6,fontSize:11,color:'var(--down-field-ink)',
          display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          Import error: {importError}
          <button onClick={()=>setImportError(null)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--down-field-ink)',fontSize:13}}>✕</button>
        </div>
      )}

      <ConfirmModal
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel="Delete"
        onConfirm={confirmModal?.onConfirm}
        onCancel={()=>setConfirmModal(null)}/>

      {/* Import review panel */}
      {importState==='review' && importPreview && (
        <div className="card" style={{padding:12,marginBottom:10}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
            <div>
              <div style={{fontSize:12,fontWeight:700,color:'var(--ink)',marginBottom:2}}>
                Import Review — {importPreview.broker}
              </div>
              <div style={{fontSize:11,color:'var(--ink-3)'}}>
                {importPreview.total_fills} fills parsed ·&nbsp;
                <span style={{color:'var(--up-field-ink)',fontWeight:600}}>{importPreview.new_fills} new</span>
                {importPreview.duplicate_fills > 0 && (
                  <span style={{color:'var(--amber-ink)',fontWeight:600}}> · {importPreview.duplicate_fills} possible duplicate{importPreview.duplicate_fills!==1?'s':''}</span>
                )}
                {' · '}Select rows to import, then click Confirm.
              </div>
            </div>
            <div style={{display:'flex',gap:6}}>
              <button className="pill on" onClick={handleImportCommit}
                disabled={importState==='committing'||!Object.values(importApproved).some(Boolean)}
                style={{opacity:importState==='committing'?0.5:1}}>
                {importState==='committing' ? '⏳ Saving…' : `Confirm (${Object.values(importApproved).filter(Boolean).length})`}
              </button>
              <button className="pill" onClick={()=>{setImportState('idle');setImportPreview(null);setImportError(null);}}>
                Cancel
              </button>
            </div>
          </div>

          <div className="tw" style={{maxHeight:360,overflowY:'auto'}}>
            <table>
              <thead>
                <tr>
                  <th style={{width:28}}>
                    <input type="checkbox"
                      checked={importPreview.positions.length>0 && importPreview.positions.every((_,i)=>importApproved[i])}
                      onChange={e=>{
                        const next={};
                        importPreview.positions.forEach((_,i)=>{next[i]=e.target.checked;});
                        setImportApproved(next);
                      }}/>
                  </th>
                  <th style={{textAlign:'left'}}>Underlying</th>
                  <th style={{textAlign:'left'}}>Instrument</th>
                  <th style={{textAlign:'center'}}>Dir</th>
                  <th style={{textAlign:'right'}}>Lots</th>
                  <th style={{textAlign:'right'}}>Entry ₹</th>
                  <th style={{textAlign:'right'}}>Exit ₹</th>
                  <th style={{textAlign:'left'}}>Opened</th>
                  <th style={{textAlign:'left'}}>Closed</th>
                  <th style={{textAlign:'right'}}>P&L ₹</th>
                  <th style={{textAlign:'center'}}>Status</th>
                  <th style={{textAlign:'center'}}>Fills</th>
                  <th style={{textAlign:'left'}}>Flag</th>
                </tr>
              </thead>
              <tbody>
                {importPreview.positions.map((p,i) => {
                  const checked = !!importApproved[i];
                  const pp = (p.pnl ?? 0) >= 0;
                  return (
                    <tr key={i} style={{opacity:checked?1:0.45,background:p.is_duplicate?'rgba(138,90,0,.06)':''}}>
                      <td style={{textAlign:'center'}}>
                        <input type="checkbox" checked={checked}
                          onChange={e=>setImportApproved(a=>({...a,[i]:e.target.checked}))}/>
                      </td>
                      <td style={{fontWeight:600}}>{p.underlying}</td>
                      <td style={{fontSize:10,color:'var(--ink-2)'}}>{p.inst}</td>
                      <td style={{textAlign:'center'}}>{p.direction==='long'?'L':'S'}</td>
                      <td style={{textAlign:'right'}}>{p.lots}</td>
                      <td style={{textAlign:'right'}}>{p.entry_price!=null?p.entry_price.toFixed(2):'—'}</td>
                      <td style={{textAlign:'right'}}>{p.exit_price!=null?p.exit_price.toFixed(2):'—'}</td>
                      <td style={{fontSize:10}}>{p.date_open||'—'}</td>
                      <td style={{fontSize:10}}>{p.date_close||'—'}</td>
                      <td style={{textAlign:'right',color:p.pnl!=null?(pp?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)',fontWeight:600}}>
                        {p.pnl!=null?(pp?'+':'')+Math.round(p.pnl).toLocaleString('en-IN'):'—'}
                      </td>
                      <td style={{textAlign:'center',fontSize:10}}>{p.status}</td>
                      <td style={{textAlign:'center',fontSize:10,color:'var(--ink-3)'}}>{p.fill_count}</td>
                      <td style={{fontSize:10}}>
                        {p.is_duplicate && <span style={{color:'var(--amber-ink)',fontWeight:600}}>⚠ Dupe</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* New trade form */}
      {showNew&&(
        <div className="card" style={{padding:12,marginBottom:10}}>
          <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:10}}>
            {editUTN !== null ? `Edit Trade — UTN ${newT.utn || editUTN}` : 'New Trade Entry'}
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:8,marginBottom:8}}>
            <F label="Underlying">
              <UnderlyingAutocomplete
                value={newT.underlying}
                onChange={v => setField('underlying', v.toUpperCase())}
                universe={symbolUniverse}
              />
            </F>
            <F label="Segment">
              <Select value={newT.segment} onChange={e=>{
                const seg = e.target.value;
                if (seg === 'Equity' || seg === 'Currency') {
                  setNewT(t => ({...t, segment: seg, type: 'EQ', bs: 'Buy'}));
                } else {
                  setField('segment', seg);
                }
              }}>
                {SEGMENTS.map(s=><option key={s}>{s}</option>)}
              </Select>
            </F>
            <F label="Strategy">
              <StrategySelect value={newT.strategy} onChange={v=>setField('strategy',v)}
                registryStrategies={registryStrategies}/>
            </F>
            <F label="Buy/Sell">
              <Select value={newT.bs} onChange={e=>setField('bs',e.target.value)}>
                <option>Buy</option><option>Sell</option>
              </Select>
            </F>
            <F label="Type">
              <Select value={newT.type} onChange={e=>setField('type',e.target.value)}>
                <option>CE</option><option>PE</option><option>FUT</option><option>EQ</option>
              </Select>
            </F>
            {['F&O','Commodity'].includes(newT.segment) && ['CE','PE'].includes(newT.type) && (
              <F label="Strike ₹">
                <input className="inp" type="number" value={newT.strike}
                  onChange={e=>setField('strike',e.target.value)} placeholder="e.g. 21500"/>
              </F>
            )}
            {['F&O','Commodity'].includes(newT.segment) && (
              <F label={expiriesLoading ? 'Expiry (loading…)' : 'Expiry'}>
                {(() => {
                  const today = new Date().toISOString().split('T')[0];
                  const past = expiries.filter(e => e < today).sort();         // ASC: oldest first
                  const upcoming = expiries.filter(e => e >= today).sort();    // ASC: nearest first
                  const isCustom = newT.expiry === '__custom__' ||
                    (newT.expiry && !expiries.includes(newT.expiry));
                  return (
                    <>
                      <Select value={isCustom ? '__custom__' : newT.expiry}
                        onChange={e => {
                          if (e.target.value === '__custom__') {
                            setField('expiry', '__custom__');
                          } else {
                            setCustomExpiry('');
                            setField('expiry', e.target.value);
                          }
                        }}>
                        <option value="">— select —</option>
                        {past.length > 0 && (
                          <optgroup label="Past (3 months)">
                            {past.map(d => <option key={d} value={d}>{fmtExpiry(d)}</option>)}
                          </optgroup>
                        )}
                        {upcoming.length > 0 && (
                          <optgroup label="Upcoming">
                            {upcoming.map(d => <option key={d} value={d}>{fmtExpiry(d)}</option>)}
                          </optgroup>
                        )}
                        {!expiriesLoading && expiries.length === 0 && newT.underlying && (
                          <option disabled>No expiries found in cache</option>
                        )}
                        <option value="__custom__">✎ Enter custom date…</option>
                      </Select>
                      {isCustom && (
                        <input className="inp" type="date" style={{marginTop:3}}
                          value={newT.expiry === '__custom__' ? customExpiry : newT.expiry}
                          onChange={e => {
                            setCustomExpiry(e.target.value);
                            if (e.target.value) setField('expiry', e.target.value);
                          }}
                          placeholder="YYYY-MM-DD"/>
                      )}
                    </>
                  );
                })()}
              </F>
            )}
            <F label="Instrument">
              <input className="inp" value={computedInst} readOnly
                title="Auto-filled from Underlying · Type · Strike · Expiry"
                style={{background:'var(--surface-2)',cursor:'default',color:'var(--ink-3)',userSelect:'none'}}/>
            </F>
            <F label="Lots/Qty"><input className="inp" type="number" value={newT.lots}    onChange={e=>setField('lots',+e.target.value)}/></F>
            {['F&O','Commodity'].includes(newT.segment) && (
              <F label="Lot Size"><input className="inp" type="number" value={newT.lotSize} onChange={e=>setField('lotSize',+e.target.value)}/></F>
            )}
            <F label="Entry ₹"><input className="inp" type="number" value={newT.entry}   onChange={e=>setField('entry',e.target.value)}/></F>
            <F label="Exit ₹"><input className="inp" type="number" value={newT.exit}    onChange={e=>setField('exit',e.target.value)}/></F>
            <F label="Date Open"><input className="inp" type="date" value={newT.dateOpen}  onChange={e=>setField('dateOpen',e.target.value)}/></F>
            <F label="Date Close"><input className="inp" type="date" value={newT.dateClose} onChange={e=>setField('dateClose',e.target.value)}/></F>
            <F label="Account">
              <Select value={accounts.includes(newT.acct)?newT.acct:'__custom_acct__'}
                onChange={e=>{
                  if(e.target.value==='__custom_acct__') setField('acct','');
                  else setField('acct',e.target.value);
                }}>
                {accounts.map(a=><option key={a} value={a}>{a}</option>)}
                <option value="__custom_acct__">+ New account name...</option>
              </Select>
              {(!accounts.includes(newT.acct)||newT.acct==='')&&(
                <input className="inp" style={{marginTop:3}} value={newT.acct}
                  placeholder="Enter new account name"
                  onChange={e=>setField('acct',e.target.value)}/>
              )}
            </F>
          </div>
          <F label="Group ID">
            <div style={{display:'flex',gap:4,alignItems:'center',marginBottom:6}}>
              <input className="inp" value={newT.groupId} placeholder={autoGroupId()}
                onChange={e=>setField('groupId',e.target.value)} style={{flex:1}}/>
              <button onClick={()=>setField('groupId','')}
                title="Revert to auto-generated Group ID"
                style={{fontSize:11,padding:'2px 6px',borderRadius:3,border:'.5px solid var(--hair-strong)',
                  color:'var(--ink-3)',background:'transparent',cursor:'pointer',flexShrink:0}}
              >↺</button>
            </div>
          </F>
          <F label="Notes">
            <input className="inp" value={newT.notes} onChange={e=>setField('notes',e.target.value)} style={{width:'100%',marginBottom:10}}/>
          </F>
          <div style={{display:'flex',gap:6}}>
            <button className="pill on" onClick={saveTrade}>
              {editUTN !== null ? `Update Trade (${newT.utn || editUTN})` : 'Save Trade'}
            </button>
            <button className="pill" onClick={cancelForm}>Cancel</button>
          </div>
        </div>
      )}



      {/* Loading state */}
      {loading && (
        <div style={{padding:'24px',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>
          ⏳ Loading trades…
        </div>
      )}

      {/* ══ TABLE */}
      {!loading && (
      <div className="tw" style={{ overflowY: 'auto', maxHeight: `calc(100vh - ${TOPBAR_H + subtabH + filtersH}px)` }}>
        <table>
          <thead style={{ position: 'sticky', top: 0, zIndex: 3, background: 'var(--surface-2)' }}>
            <tr>
              {view === 'trade' ? (
                <th style={{width:28,padding:'5px 4px'}}>
                  <input type="checkbox"
                    checked={sortedTrades.length > 0 && sortedTrades.every(t => selectedIds.has(t.id))}
                    onChange={e => {
                      if (e.target.checked) setSelectedIds(new Set(sortedTrades.map(t => t.id)));
                      else setSelectedIds(new Set());
                    }}/>
                </th>
              ) : (
                <th style={{width:28}}/>
              )}
              <SortTH col="utn"       style={{textAlign:'left',  width:70,  maxWidth:90 }}>UTN</SortTH>
              <SortTH col="underlying"style={{textAlign:'left',  width:90,  maxWidth:130}}>Underlying</SortTH>
              <SortTH col="groupId"   style={{textAlign:'left',  width:110, maxWidth:160}}>Group ID</SortTH>
              <SortTH col="strategy"  style={{textAlign:'left',  width:90,  maxWidth:130}}>Strategy</SortTH>
              <SortTH col="segment"   style={{textAlign:'left',  width:64,  maxWidth:80 }}>Segment</SortTH>
              <th                     style={{textAlign:'left',  width:120, maxWidth:180}}>Instrument</th>
              <th                     style={{textAlign:'center',width:50,  maxWidth:60 }}>L/S</th>
              <th                     style={{textAlign:'right', width:52,  maxWidth:64 }}>Lots/Qty</th>
              <th                     style={{textAlign:'right', width:48,  maxWidth:60 }}>Lot Sz</th>
              <SortTH col="acct"      style={{textAlign:'left',  width:64,  maxWidth:90 }}>Account</SortTH>
              <SortTH col="dateOpen"  style={{textAlign:'left',  width:74,  maxWidth:84 }}>Open</SortTH>
              <SortTH col="dateClose" style={{textAlign:'left',  width:74,  maxWidth:84 }}>Close</SortTH>
              <th                     style={{textAlign:'right', width:60,  maxWidth:80 }}>Entry ₹</th>
              <th                     style={{textAlign:'right', width:56,  maxWidth:76 }}>Exit ₹</th>
              <SortTH col="pnl"       style={{textAlign:'right', width:90,  maxWidth:110}}>P&L ₹</SortTH>
              <SortTH col="status"    style={{textAlign:'left',  width:54,  maxWidth:70 }}>Status</SortTH>
              <th                     style={{textAlign:'center',width:28}}>ℹ</th>
              <th style={{width:view==='group'?88:28}}>
                {view==='group' && (
                  <button onClick={()=>{
                    const allExp = groupedRows.every(g=>expandedGroups[g.groupId]!==false);
                    const next={};
                    groupedRows.forEach(g=>{next[g.groupId]=!allExp;});
                    setExpandedGroups(next);
                  }} style={{fontSize:11,padding:'1px 6px',borderRadius:3,border:'.5px solid var(--hair-strong)',
                    color:'var(--ink-3)',background:'transparent',cursor:'pointer',whiteSpace:'nowrap'}}>
                    {groupedRows.every(g=>expandedGroups[g.groupId]!==false)?'− Collapse All':'+ Expand All'}
                  </button>
                )}
              </th>
            </tr>
          </thead>
          <tbody>

            {/* ── TRADE VIEW rows ── */}
            {view==='trade' && sortedTrades.map((t,i)=>{
              const liveData  = t.status==='Open' ? (livePnl[t.id] ?? null) : null;
              const livePnlVal = liveData?.live_pnl ?? null;
              const displayPnl = livePnlVal !== null ? livePnlVal : t.pnl;
              const pp = (displayPnl ?? 0) >= 0;
              return (
                <tr key={i}>
                  <td style={{padding:'5px 4px',textAlign:'center'}}>
                    <input type="checkbox" checked={selectedIds.has(t.id)}
                      onChange={e => setSelectedIds(s => {
                        const n = new Set(s);
                        if (e.target.checked) n.add(t.id); else n.delete(t.id);
                        return n;
                      })}/>
                  </td>
                  <td className="thl" style={{fontFamily:'var(--font-data)',fontSize:11,color:'var(--ink-3)'}}>{t.utn}</td>
                  <td className="thl" style={{fontWeight:700,fontSize:11,color:'var(--amber-ink)',cursor:'pointer'}}
                    onClick={()=>openPanel({name:t.underlying,sector:''})}>{t.underlying}</td>
                  <td className="thl" style={{fontFamily:'var(--font-data)',fontSize:11,color:'var(--ink-3)',
                    maxWidth:150,overflow:'hidden',textOverflow:'ellipsis'}}>{t.groupId}</td>
                  <td className="thl" style={{fontSize:11,color:'var(--ink-2)'}}>{t.strategy}</td>
                  <td className="thl" style={{fontSize:11,color:'var(--ink-3)'}}>{t.segment}</td>
                  <td className="thl" style={{fontSize:11}}>{t.inst}</td>
                  <td style={{textAlign:'center'}}>
                    <span style={{fontSize:11,fontWeight:700,padding:'1px 5px',borderRadius:3,
                      background:t.bs==='Buy'?'rgba(26,106,26,.1)':'rgba(136,30,16,.1)',
                      color:t.bs==='Buy'?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                      {t.bs==='Buy'?'Long':'Short'}
                    </span>
                  </td>
                  <td style={{fontSize:11}}>{t.lots}</td>
                  <td style={{fontSize:11,color:'var(--ink-3)'}}>{t.lotSize}</td>
                  <td className="thl" style={{fontSize:11,color:'var(--ink-3)'}}>{t.acct}</td>
                  <td className="thl" style={{fontSize:11}}>{fmtD(t.dateOpen)}</td>
                  <td className="thl" style={{fontSize:11,color:'var(--ink-3)'}}>{fmtD(t.dateClose)}</td>
                  <td style={{fontWeight:600}}>₹{t.entry}</td>
                  <td style={{fontSize:11}}>{t.exit?`₹${t.exit}`:'—'}</td>
                  <td style={{fontWeight:700,fontFamily:'var(--font-data)',color:pp?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                    {displayPnl != null ? (
                      <span style={{display:'flex',alignItems:'center',justifyContent:'flex-end',gap:3}}>
                        {livePnlVal !== null && (
                          <span style={{fontSize:11,fontWeight:400,color:'var(--ink-3)',flexShrink:0,
                            background:'rgba(74,144,217,.12)',borderRadius:2,padding:'0 3px'}}>LIVE</span>
                        )}
                        <span>{pp?'+':'−'}₹{Math.abs(displayPnl).toLocaleString()}</span>
                      </span>
                    ) : '—'}
                  </td>
                  <td className="thl">
                    <span style={{fontSize:11,padding:'1px 6px',borderRadius:3,fontWeight:600,
                      background:t.status==='Open'?'#e3f2fd':'var(--surface-2)',
                      color:t.status==='Open'?'#1565c0':'var(--ink-3)'}}>
                      {t.status}
                    </span>
                  </td>
                  <NotesCell notes={t.notes}/>
                  <td className="thl" style={{whiteSpace:'nowrap'}}>
                    <button onClick={()=>editTrade(t)} style={{fontSize:11,padding:'1px 5px',borderRadius:3,border:'.5px solid var(--amber-ink)',color:'var(--amber-ink)',background:'transparent',cursor:'pointer',marginRight:3}}>✎</button>
                    <button onClick={()=>copyTrade(t)} style={{fontSize:11,padding:'1px 5px',borderRadius:3,border:'.5px solid var(--hair-strong)',color:'var(--ink-3)',background:'transparent',cursor:'pointer',marginRight:3}}>⎘</button>
                    <button onClick={()=>deleteTrade(t.id)} style={{fontSize:11,padding:'1px 5px',borderRadius:3,border:'.5px solid var(--down-field-ink)',color:'var(--down-field-ink)',background:'transparent',cursor:'pointer'}}>✕</button>
                  </td>
                </tr>
              );
            })}

            {/* ── GROUP VIEW rows ── */}
            {view==='group' && groupedRows.map((g,gi)=>{
              const pp     = g.netPnl>=0;
              const isOpen = expandedGroups[g.groupId]!==false;
              return (
                <React.Fragment key={gi}>

                  {/* Group header row */}
                  <tr style={{background:'var(--surface-2)',cursor:'pointer'}}
                    onClick={()=>setExpandedGroups(e=>({...e,[g.groupId]:!isOpen}))}>
                    {/* UTN: chevron */}
                    <td style={{textAlign:'center',color:'var(--ink-3)',fontSize:11,fontWeight:700}}>{isOpen?'▾':'▸'}</td>
                    {/* Underlying: stock name bold accent */}
                    <td className="thl" style={{fontWeight:700,color:'var(--amber-ink)',cursor:'pointer'}}
                      onClick={e=>{e.stopPropagation();openPanel({name:g.underlying,sector:''});}}>
                      {g.underlying}
                    </td>
                    {/* Group ID: normal muted mono */}
                    <td className="thl" style={{fontFamily:'var(--font-data)',fontSize:11,color:'var(--ink-3)',
                      maxWidth:150,overflow:'hidden',textOverflow:'ellipsis'}}>{g.groupId}</td>
                    {/* Strategy */}
                    <td className="thl" style={{fontSize:11,fontWeight:600,color:'var(--ink-2)'}}>{g.strategy}</td>
                    {/* Segment — shown on group header row */}
                    <td className="thl" style={{fontSize:11,color:'var(--ink-3)'}}>{g.segment}</td>
                    {/* Instrument: N legs */}
                    <td className="thl" style={{fontSize:11,color:'var(--ink-3)',fontWeight:600}}>
                      {g.legs.length} leg{g.legs.length>1?'s':''}
                    </td>
                    {/* L/S, Lots, Lot Sz: blank */}
                    <td/><td/><td/>
                    {/* Account */}
                    <td className="thl" style={{fontSize:11,color:'var(--ink-3)'}}>{g.acct}</td>
                    {/* Open */}
                    <td className="thl" style={{fontSize:11,fontWeight:600,color:'var(--ink)'}}>{fmtD(g.dateOpen)}</td>
                    {/* Close */}
                    <td className="thl" style={{fontSize:11,color:'var(--ink)'}}>{fmtD(g.dateClose)}</td>
                    {/* Entry, Exit: blank */}
                    <td/><td/>
                    {/* Net P&L */}
                    <td style={{fontWeight:700,fontSize:11,color:pp?'var(--up-field-ink)':'var(--down-field-ink)',
                      fontFamily:'var(--font-data)',whiteSpace:'nowrap'}}>
                      {pp?'+':'−'}₹{Math.abs(g.netPnl).toLocaleString()}
                    </td>
                    {/* Status */}
                    <td className="thl">
                      <span style={{fontSize:11,padding:'1px 6px',borderRadius:3,fontWeight:700,
                        background:g.status==='Open'?'rgba(26,74,170,.1)':'rgba(26,106,26,.1)',
                        color:g.status==='Open'?'#1a4aaa':'var(--up-field-ink)'}}>
                        {g.status}
                      </span>
                    </td>
                    <td className="thl"/><td className="thl"/>
                  </tr>

                  {/* Leg rows */}
                  {isOpen && g.legs.map((leg,li)=>{
                    const legLiveData  = leg.status==='Open' ? (livePnl[leg.id] ?? null) : null;
                    const legLivePnl   = legLiveData?.live_pnl ?? null;
                    const legDisplayPnl = legLivePnl !== null ? legLivePnl : (leg.pnl || null);
                    const lpp = (legDisplayPnl ?? 0) >= 0;
                    return (
                      <tr key={li} style={{borderBottom:'.5px solid var(--hair)'}}>
                        {/* UTN */}
                        <td className="thl" style={{fontFamily:'var(--font-data)',fontSize:11,color:'var(--ink-3)',paddingLeft:8}}>{leg.utn}</td>
                        {/* Underlying: blank */}
                        <td className="thl"/>
                        {/* Group ID: blank */}
                        <td className="thl"/>
                        {/* Strategy: blank */}
                        <td className="thl"/>
                        {/* Segment: blank on leg rows */}
                        <td className="thl"/>
                        {/* Instrument: ↳ indented */}
                        <td className="thl" style={{paddingLeft:16,fontSize:11,color:'var(--ink-2)'}}>
                          <span style={{color:'var(--ink-3)',marginRight:4,fontSize:11}}>↳</span>
                          {leg.inst||'—'}
                        </td>
                        {/* L/S */}
                        <td style={{textAlign:'center'}}>
                          <span style={{fontSize:11,fontWeight:700,padding:'1px 5px',borderRadius:3,
                            background:leg.bs==='Buy'?'rgba(26,106,26,.1)':'rgba(136,30,16,.1)',
                            color:leg.bs==='Buy'?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                            {leg.bs==='Buy'?'Long':'Short'}
                          </span>
                        </td>
                        {/* Lots */}
                        <td style={{fontSize:11}}>{leg.lots}</td>
                        {/* Lot Sz */}
                        <td style={{fontSize:11,color:'var(--ink-3)'}}>{leg.lotSize}</td>
                        {/* Account: blank */}
                        <td className="thl"/>
                        {/* Open */}
                        <td className="thl" style={{fontSize:11,color:'var(--ink-3)'}}>{fmtD(leg.dateOpen)}</td>
                        {/* Close */}
                        <td className="thl" style={{fontSize:11,color:'var(--ink-3)'}}>{fmtD(leg.dateClose)}</td>
                        {/* Entry */}
                        <td style={{fontWeight:600,fontSize:11}}>₹{leg.entry}</td>
                        {/* Exit */}
                        <td style={{fontSize:11}}>{leg.exit?`₹${leg.exit}`:'—'}</td>
                        {/* Leg P&L */}
                        <td style={{fontFamily:'var(--font-data)',fontSize:11,
                          color:legDisplayPnl!=null?lpp?'var(--up-field-ink)':'var(--down-field-ink)':'var(--ink-3)',
                          fontWeight:legDisplayPnl!=null?700:400}}>
                          {legDisplayPnl!=null ? (
                            <span>
                              {lpp?'+':'−'}₹{Math.abs(legDisplayPnl).toLocaleString()}
                              {legLivePnl !== null && (
                                <span style={{fontSize:11,fontWeight:400,color:'var(--ink-3)',marginLeft:3,
                                  background:'rgba(74,144,217,.12)',borderRadius:2,padding:'0 3px'}}>LIVE</span>
                              )}
                            </span>
                          ) : 'Open'}
                        </td>
                        {/* Status */}
                        <td className="thl">
                          <span style={{fontSize:11,padding:'1px 5px',borderRadius:3,
                            background:leg.status==='Open'?'#e3f2fd':'var(--surface-2)',
                            color:leg.status==='Open'?'#1565c0':'var(--ink-3)'}}>
                            {leg.status}
                          </span>
                        </td>
                        <NotesCell notes={leg.notes}/>
                        {/* Edit + Copy */}
                        <td className="thl" style={{whiteSpace:'nowrap'}}>
                          <button onClick={()=>editTrade(leg)} style={{fontSize:11,padding:'1px 5px',borderRadius:3,border:'.5px solid var(--amber-ink)',color:'var(--amber-ink)',background:'transparent',cursor:'pointer',marginRight:3}}>✎</button>
                          <button onClick={()=>copyTrade(leg)} style={{fontSize:11,padding:'1px 5px',borderRadius:3,border:'.5px solid var(--hair-strong)',color:'var(--ink-3)',background:'transparent',cursor:'pointer',marginRight:3}}>⎘</button>
                          <button onClick={()=>deleteTrade(leg.id)} style={{fontSize:11,padding:'1px 5px',borderRadius:3,border:'.5px solid var(--down-field-ink)',color:'var(--down-field-ink)',background:'transparent',cursor:'pointer'}}>✕</button>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              );
            })}

          </tbody>
        </table>
      </div>
      )} {/* end !loading */}
    </div>
  );
}


/* ── Analytics ── */
function AnalyticsTab({ onDrilldown, subtabH = 40 }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [range, setRange]     = useState('all');

  const load = useCallback(() => {
    setLoading(true); setError(null);
    const q = range !== 'all' ? `?range=${range}` : '';
    apiFetch(`/api/journal/analytics${q}`)
      .then(r => { if (!r.ok) throw new Error(r.statusText); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [range]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div style={{padding:'24px',textAlign:'center',color:'var(--ink-3)',fontSize:11}}>⏳ Loading analytics…</div>
  );
  if (error) return (
    <div style={{padding:'16px',background:'rgba(136,30,16,.07)',borderRadius:6,color:'var(--down-field-ink)',fontSize:11}}>
      Failed to load analytics: {error}
      <button onClick={load} style={{marginLeft:10,fontSize:11,padding:'2px 8px',border:'.5px solid var(--down-field-ink)',borderRadius:3,background:'transparent',color:'var(--down-field-ink)',cursor:'pointer'}}>Retry</button>
    </div>
  );

  const a = data;

  // Empty state — no closed trades yet
  if (a.closedCount === 0) return (
    <div className="card" style={{padding:'32px',textAlign:'center'}}>
      <div style={{fontSize:14,color:'var(--ink-3)',marginBottom:6}}>No closed trades yet</div>
      <div style={{fontSize:11,color:'var(--ink-3)'}}>
        Analytics are computed from closed trades. Close a trade in the Journal tab to see stats here.
        {a.openCount > 0 && ` (${a.openCount} open position${a.openCount!==1?'s':''} tracked)`}
      </div>
    </div>
  );

  const maxW = a.weekly.length ? Math.max(...a.weekly.map(w => Math.abs(w.pnl)), 1) : 1;

  const MetricCard = ({ l, v, sub, c }) => (
    <div className="card" style={{padding:'8px 10px'}}>
      <div style={{fontSize:11,color:'var(--ink-3)',textTransform:'uppercase',letterSpacing:'.04em',marginBottom:3}}>{l}</div>
      <div style={{fontFamily:'var(--font-data)',fontWeight:700,fontSize:16,color:c||'var(--ink)'}}>{v}</div>
      {sub && <div style={{fontSize:11,color:'var(--ink-3)',marginTop:2}}>{sub}</div>}
    </div>
  );

  return (
    <div style={{display:'flex',flexDirection:'column',gap:12}}>

      {/* Time-range filter — sticky */}
      <div style={{ position: 'sticky', top: TOPBAR_H + subtabH, zIndex: 8,
        background: 'var(--paper)', paddingBottom: 8 }}>
      <div style={{display:'flex',gap:4,alignItems:'center'}}>
        {['1M','3M','6M','1Y','all'].map(r => (
          <button key={r} onClick={()=>setRange(r)}
            style={{fontSize:11,padding:'3px 10px',borderRadius:4,cursor:'pointer',border:'none',
              fontWeight:range===r?700:400,
              background:range===r?'var(--amber-ink)':'var(--surface-2)',
              color:range===r?'#fff':'var(--ink-2)'}}>
            {r==='all'?'All':r}
          </button>
        ))}
        {loading && <span style={{fontSize:11,color:'var(--ink-3)',marginLeft:6}}>⏳</span>}
      </div>
      </div>{/* end sticky time-range wrapper */}

      {/* Summary cards */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(130px,1fr))',gap:8}}>
        <MetricCard l="Total trades" v={a.total}
          sub={`${a.openCount} open · ${a.closedCount} closed`}/>
        <MetricCard l="Win rate"
          v={a.closedCount>=5 ? `${a.winRate}%` : 'N/A'}
          c={a.closedCount>=5 ? (a.winRate>=50?'var(--up-field-ink)':'var(--down-field-ink)') : 'var(--ink-3)'}
          sub={a.closedCount>=5 ? `${a.wins} wins / ${a.closedCount} closed` : 'Need 5+ closed trades'}/>
        <MetricCard l="Net P&L"
          v={`${a.netPnl>=0?'+':''}₹${a.netPnl.toLocaleString()}`}
          c={a.netPnl>=0?'var(--up-field-ink)':'var(--down-field-ink)'}
          sub="Closed trades only"/>
        <MetricCard l="Avg winner"
          v={a.wins>=3 ? `+₹${a.avgWin.toLocaleString()}` : 'N/A'}
          c={a.wins>=3 ? 'var(--up-field-ink)' : 'var(--ink-3)'}
          sub={a.wins>=3 ? `${a.wins} winning trade${a.wins!==1?'s':''}` : 'Need 3+ winners'}/>
        <MetricCard l="Avg loser"
          v={a.losses>=3 ? `-₹${Math.abs(a.avgLoss).toLocaleString()}` : 'N/A'}
          c={a.losses>=3 ? 'var(--down-field-ink)' : 'var(--ink-3)'}
          sub={a.losses>=3 ? `${a.losses} losing trade${a.losses!==1?'s':''}` : 'Need 3+ losers'}/>
        {a.bestTrade && (
          <MetricCard l="Best trade"
            v={`+₹${a.bestTrade.pnl.toLocaleString()}`}
            c="var(--up-field-ink)" sub={a.bestTrade.name}/>
        )}
        {a.worstTrade && (
          <MetricCard l="Worst trade"
            v={`${a.worstTrade.pnl>=0?'+':''}₹${a.worstTrade.pnl.toLocaleString()}`}
            c="var(--down-field-ink)" sub={a.worstTrade.name}/>
        )}
      </div>

      {/* Weekly P&L bar chart */}
      {a.weekly.length > 0 && (
        <div className="card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:10}}>
            Weekly P&L <span style={{fontWeight:400,color:'var(--ink-3)'}}>· last {a.weekly.length} weeks (closed trades)</span>
          </div>
          <div style={{display:'flex',gap:8,alignItems:'flex-end',height:90}}>
            {a.weekly.map((w, i) => {
              const pos = w.pnl >= 0;
              const h   = Math.max(4, Math.abs(w.pnl) / maxW * 70);
              return (
                <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:3}}>
                  <div style={{fontSize:11,color:pos?'var(--up-field-ink)':'var(--down-field-ink)',
                    fontFamily:'var(--font-data)',fontWeight:600,whiteSpace:'nowrap'}}>
                    {pos?'+':''}₹{(w.pnl/1000).toFixed(1)}k
                  </div>
                  <div style={{width:'100%',height:h,
                    background:pos?'rgba(26,106,26,.55)':'rgba(136,30,16,.55)',
                    borderRadius:'3px 3px 0 0'}}/>
                  <div style={{fontSize:11,color:'var(--ink-3)',whiteSpace:'nowrap'}}>{w.week}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Equity Curve */}
      {a.equityCurve && a.equityCurve.length > 1 && (() => {
        const pts   = a.equityCurve;
        const vals  = pts.map(p => p.cumPnl);
        const minV  = Math.min(0, ...vals);
        const maxV  = Math.max(0, ...vals);
        const range = maxV - minV || 1;
        const W = 600, H = 90, PAD = 4;
        const x = i  => PAD + (i / (pts.length - 1)) * (W - PAD * 2);
        const y = v  => PAD + (1 - (v - minV) / range) * (H - PAD * 2);
        const polyline = pts.map((p, i) => `${x(i)},${y(p.cumPnl)}`).join(' ');
        const zeroY    = y(0);
        const finalPos = vals[vals.length - 1] >= 0;
        // Closed fill path: line + close back along zero
        const fillPath = `M ${x(0)},${zeroY} ` +
          pts.map((p, i) => `L ${x(i)},${y(p.cumPnl)}`).join(' ') +
          ` L ${x(pts.length - 1)},${zeroY} Z`;
        return (
          <div className="card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:8}}>
              Equity Curve
              <span style={{fontWeight:400,color:'var(--ink-3)',marginLeft:6}}>
                · {pts.length} closed trades · final{' '}
                <span style={{color:finalPos?'var(--up-field-ink)':'var(--down-field-ink)',fontFamily:'var(--font-data)'}}>
                  {finalPos?'+':'−'}₹{Math.abs(vals[vals.length-1]).toLocaleString()}
                </span>
              </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H,display:'block'}}>
              {/* Zero line */}
              <line x1={PAD} y1={zeroY} x2={W-PAD} y2={zeroY}
                stroke="var(--hair)" strokeWidth="0.8" strokeDasharray="3 3"/>
              {/* Fill under curve */}
              <path d={fillPath}
                fill={finalPos?'rgba(26,106,26,.12)':'rgba(136,30,16,.12)'}/>
              {/* Curve line */}
              <polyline points={polyline}
                fill="none"
                stroke={finalPos?'var(--up-field-ink)':'var(--down-field-ink)'}
                strokeWidth="1.5"
                strokeLinejoin="round"/>
              {/* Start + end dots */}
              <circle cx={x(0)} cy={y(vals[0])} r="2.5"
                fill={vals[0]>=0?'var(--up-field-ink)':'var(--down-field-ink)'}/>
              <circle cx={x(pts.length-1)} cy={y(vals[vals.length-1])} r="3"
                fill={finalPos?'var(--up-field-ink)':'var(--down-field-ink)'}/>
            </svg>
            {/* X-axis: first, mid, last date labels */}
            <div style={{display:'flex',justifyContent:'space-between',
              fontSize:11,color:'var(--ink-3)',marginTop:2,padding:`0 ${PAD}px`}}>
              <span>{pts[0].date}</span>
              {pts.length > 2 && <span>{pts[Math.floor(pts.length/2)].date}</span>}
              <span>{pts[pts.length-1].date}</span>
            </div>
          </div>
        );
      })()}

      {/* Drawdown Chart */}
      {a.equityCurve && a.equityCurve.length > 1 && a.maxDrawdown < 0 && (() => {
        const pts = a.equityCurve;
        const dds = pts.map(p => p.drawdown);
        const minDD = Math.min(...dds, 0);
        const W = 600, H = 70, PAD = 4;
        const x = i => PAD + (i / (pts.length - 1)) * (W - PAD * 2);
        const y = v => PAD + (v / minDD) * (H - PAD * 2);   // 0→top, minDD→bottom
        const fillPath = `M ${x(0)},${PAD} ` +
          pts.map((p, i) => `L ${x(i)},${y(p.drawdown)}`).join(' ') +
          ` L ${x(pts.length-1)},${PAD} Z`;
        const worstIdx = dds.indexOf(minDD);
        return (
          <div className="card" style={{padding:'10px 14px'}}>
            <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:8}}>
              Drawdown
              <span style={{fontWeight:400,color:'var(--ink-3)',marginLeft:6}}>
                · Max{' '}
                <span style={{color:'var(--down-field-ink)',fontFamily:'var(--font-data)'}}>
                  −₹{Math.abs(a.maxDrawdown).toLocaleString()}
                </span>
                {worstIdx >= 0 && <span> on {pts[worstIdx].date}</span>}
              </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%',height:H,display:'block'}}>
              <line x1={PAD} y1={PAD} x2={W-PAD} y2={PAD}
                stroke="var(--hair)" strokeWidth="0.8" strokeDasharray="3 3"/>
              <path d={fillPath} fill="rgba(136,30,16,.18)"/>
              <polyline
                points={pts.map((p,i)=>`${x(i)},${y(p.drawdown)}`).join(' ')}
                fill="none" stroke="var(--down-field-ink)" strokeWidth="1.2" strokeLinejoin="round"/>
            </svg>
            <div style={{display:'flex',justifyContent:'space-between',
              fontSize:11,color:'var(--ink-3)',marginTop:2,padding:`0 ${PAD}px`}}>
              <span>{pts[0].date}</span>
              {pts.length > 2 && <span>{pts[Math.floor(pts.length/2)].date}</span>}
              <span>{pts[pts.length-1].date}</span>
            </div>
          </div>
        );
      })()}

      {/* Holding Period */}
      {(a.avgHoldWins != null || a.avgHoldLosses != null) && (
        <div className="card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:10,
            textTransform:'uppercase',letterSpacing:'.04em'}}>Holding Period</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            {/* Avg days summary cards */}
            {a.avgHoldWins != null && (
              <div style={{flex:'1 1 120px',background:'rgba(26,106,26,.07)',borderRadius:6,
                padding:'8px 12px',border:'.5px solid rgba(26,106,26,.2)'}}>
                <div style={{fontSize:11,color:'var(--ink-3)',textTransform:'uppercase',
                  letterSpacing:'.04em',marginBottom:4}}>Avg Hold — Winners</div>
                <div style={{fontFamily:'var(--font-data)',fontWeight:700,
                  fontSize:18,color:'var(--up-field-ink)'}}>{a.avgHoldWins}d</div>
              </div>
            )}
            {a.avgHoldLosses != null && (
              <div style={{flex:'1 1 120px',background:'rgba(136,30,16,.07)',borderRadius:6,
                padding:'8px 12px',border:'.5px solid rgba(136,30,16,.2)'}}>
                <div style={{fontSize:11,color:'var(--ink-3)',textTransform:'uppercase',
                  letterSpacing:'.04em',marginBottom:4}}>Avg Hold — Losers</div>
                <div style={{fontFamily:'var(--font-data)',fontWeight:700,
                  fontSize:18,color:'var(--down-field-ink)'}}>{a.avgHoldLosses}d</div>
              </div>
            )}
            {/* Bucket distribution */}
            {a.holdBuckets && a.holdBuckets.some(b => b.wins + b.losses > 0) && (
              <div style={{flex:'2 1 240px'}}>
                <div style={{fontSize:11,color:'var(--ink-3)',marginBottom:6,
                  textTransform:'uppercase',letterSpacing:'.04em'}}>Distribution</div>
                <div style={{display:'flex',flexDirection:'column',gap:4}}>
                  {a.holdBuckets.map((b, i) => {
                    const total = b.wins + b.losses;
                    if (total === 0) return null;
                    const maxB  = Math.max(...a.holdBuckets.map(x => x.wins + x.losses), 1);
                    const barW  = Math.round((total / maxB) * 100);
                    return (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:6}}>
                        <div style={{width:34,fontSize:11,color:'var(--ink-3)',
                          fontFamily:'var(--font-data)',flexShrink:0}}>{b.label}</div>
                        <div style={{flex:1,height:14,background:'var(--surface-2)',
                          borderRadius:3,overflow:'hidden',position:'relative'}}>
                          {/* wins (green) */}
                          <div style={{position:'absolute',left:0,top:0,height:'100%',
                            width:`${Math.round((b.wins/total)*barW)}%`,
                            background:'rgba(26,106,26,.55)',borderRadius:'3px 0 0 3px'}}/>
                          {/* losses (red) */}
                          <div style={{position:'absolute',left:`${Math.round((b.wins/total)*barW)}%`,
                            top:0,height:'100%',
                            width:`${Math.round((b.losses/total)*barW)}%`,
                            background:'rgba(136,30,16,.55)'}}/>
                        </div>
                        <div style={{fontSize:11,color:'var(--ink-3)',
                          fontFamily:'var(--font-data)',flexShrink:0,width:36,
                          textAlign:'right'}}>
                          <span style={{color:'var(--up-field-ink)'}}>{b.wins}W</span>
                          {' '}<span style={{color:'var(--down-field-ink)'}}>{b.losses}L</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* By Account */}
      {a.byAccount && a.byAccount.length > 0 && (
        <div className="card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:10,
            textTransform:'uppercase',letterSpacing:'.04em'}}>Performance by Account</div>
          <div className="tw">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead><tr>
                {[['Account','left'],['Trades','right'],['Win Rate','right'],
                  ['Net P&L','right'],['Avg / Trade','right']].map(([h,al])=>(
                  <th key={h} style={{textAlign:al,padding:'5px 8px',color:'var(--ink-3)',
                    fontWeight:700,fontSize:11,letterSpacing:'.04em',textTransform:'uppercase',
                    borderBottom:'.5px solid var(--hair)',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {a.byAccount.map((d, i) => (
                  <tr key={i} onClick={()=>onDrilldown&&onDrilldown({acct:d.name,status:'Closed'})}
                    style={{borderBottom:'.5px solid var(--hair)',cursor:onDrilldown?'pointer':'default'}}
                    title={onDrilldown?`View ${d.name} trades in Journal`:undefined}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <td style={{padding:'6px 8px',fontWeight:600,color:'var(--ink)'}}>{d.name}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',color:'var(--ink-2)'}}>{d.trades}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      color:d.trades>=3?(d.winRate>=50?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)'}}>
                      {d.trades>=3?`${d.winRate}%`:'N/A'}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      fontFamily:'var(--font-data)',
                      color:d.pnl>=0?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                      {d.pnl>=0?'+':'−'}₹{Math.abs(d.pnl).toLocaleString()}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',
                      fontFamily:'var(--font-data)',
                      color:d.avgReturn>=0?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                      {d.avgReturn>=0?'+':'−'}₹{Math.abs(d.avgReturn).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By Strategy */}
      {a.byStrategy.length > 0 && (
        <div className="card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:10,
            textTransform:'uppercase',letterSpacing:'.04em'}}>Performance by Strategy</div>
          <div className="tw">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead><tr>
                {[['Strategy','left'],['Trades','right'],['Win Rate','right'],
                  ['2× Rate','right'],['Net P&L','right'],['Avg Return','right'],
                  ['Best','right'],['Worst','right']].map(([h,a])=>(
                  <th key={h} style={{textAlign:a,padding:'5px 8px',color:'var(--ink-3)',
                    fontWeight:700,fontSize:11,letterSpacing:'.04em',textTransform:'uppercase',
                    borderBottom:'.5px solid var(--hair)',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {a.byStrategy.map((d, i) => (
                  <tr key={i} onClick={()=>onDrilldown&&onDrilldown({strategy:d.name,status:'Closed'})}
                    style={{borderBottom:'.5px solid var(--hair)',cursor:onDrilldown?'pointer':'default'}}
                    title={onDrilldown?`View ${d.name} trades in Journal`:undefined}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <td style={{padding:'6px 8px',fontWeight:600,color:'var(--ink)',textAlign:'left'}}>{d.name}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',color:'var(--ink-2)'}}>{d.trades}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      color:d.trades>=3?(d.winRate>=50?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)'}}>
                      {d.trades>=3?`${d.winRate}%`:'N/A'}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      color:'var(--amber-ink)'}}>{d.twoX||0}%</td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      fontFamily:'var(--font-data)',
                      color:d.pnl>=0?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                      {d.pnl>=0?'+':''}₹{d.pnl.toLocaleString()}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',
                      fontFamily:'var(--font-data)',
                      color:d.avgReturn>=0?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                      {d.avgReturn>=0?'+':''}₹{d.avgReturn?.toLocaleString?.()??'—'}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',
                      fontFamily:'var(--font-data)',color:'var(--up-field-ink)'}}>
                      {d.best!=null?`+₹${d.best.toLocaleString()}`:'—'}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',
                      fontFamily:'var(--font-data)',color:'var(--down-field-ink)'}}>
                      {d.worst!=null?`₹${d.worst.toLocaleString()}`:'—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* By Underlying */}
      {a.byUnderlying.length > 0 && (
        <div className="card" style={{padding:'10px 14px'}}>
          <div style={{fontSize:11,fontWeight:600,color:'var(--ink)',marginBottom:10,
            textTransform:'uppercase',letterSpacing:'.04em'}}>Performance by Underlying</div>
          <div className="tw">
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
              <thead><tr>
                {[['Underlying','left'],['Trades','right'],['Win Rate','right'],
                  ['Net P&L','right'],['Avg Return %','right']].map(([h,a])=>(
                  <th key={h} style={{textAlign:a,padding:'5px 8px',color:'var(--ink-3)',
                    fontWeight:700,fontSize:11,letterSpacing:'.04em',textTransform:'uppercase',
                    borderBottom:'.5px solid var(--hair)',whiteSpace:'nowrap'}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {a.byUnderlying.map((d, i) => (
                  <tr key={i} onClick={()=>onDrilldown&&onDrilldown({underlying:d.name,status:'Closed'})}
                    style={{borderBottom:'.5px solid var(--hair)',cursor:onDrilldown?'pointer':'default'}}
                    title={onDrilldown?`View ${d.name} trades in Journal`:undefined}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--surface-2)'}
                    onMouseLeave={e=>e.currentTarget.style.background=''}>
                    <td style={{padding:'6px 8px',fontWeight:600,color:'var(--amber-ink)',textAlign:'left'}}>{d.name}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',color:'var(--ink-2)'}}>{d.trades}</td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      color:d.trades>=3?(d.winRate>=50?'var(--up-field-ink)':'var(--down-field-ink)'):'var(--ink-3)'}}>
                      {d.trades>=3?`${d.winRate}%`:'N/A'}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      fontFamily:'var(--font-data)',
                      color:d.pnl>=0?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                      {d.pnl>=0?'+':''}₹{d.pnl.toLocaleString()}
                    </td>
                    <td style={{padding:'6px 8px',textAlign:'right',fontWeight:600,
                      fontFamily:'var(--font-data)',
                      color:d.avgReturnPct>=0?'var(--up-field-ink)':'var(--down-field-ink)'}}>
                      {d.avgReturnPct>=0?'+':''}{d.avgReturnPct}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── AI Trade Tab — action pill as bullet, date format, clickable tickers, generate button ── */
function AITradeTab() {
  const { open: openPanel } = useSidePanel();
  const [expanded,   setExpanded]   = useState({0:true});
  const [days,       setDays]       = useState([]);
  const [status,     setStatus]     = useState('loading');
  const [generating, setGenerating] = useState(false);
  const [genMsg,     setGenMsg]     = useState('');

  const loadDays = () => {
    return getTabReports('journal', 10).then(res => {
      const live = (res.reports || []).map(r => {
        let parsed = {};
        try { parsed = typeof r.report_json === 'string' ? JSON.parse(r.report_json) : r.report_json; } catch {}
        return { date: r.report_date, items: parsed.obs || [] };
      }).filter(d => d.items.length > 0);
      setDays(live);
      setStatus(live.length ? 'ok' : 'empty');
      return live;
    });
  };

  useEffect(() => {
    loadDays().catch(() => setStatus('empty'));
  }, []); // eslint-disable-line

  const handleGenerate = async () => {
    setGenerating(true);
    setGenMsg('Generating… (20–60s)');
    const prevDate = days[0]?.date || '';
    try {
      await generateTabReport('journal', null);
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const res = await getTabReports('journal', 1);
          const latest = res.reports?.[0];
          if (latest && (attempts > 3 || (latest.report_date || '') !== prevDate)) {
            clearInterval(poll);
            await loadDays();
            setGenerating(false);
            setGenMsg('✓ Generated!');
            setTimeout(() => setGenMsg(''), 4000);
            return;
          }
        } catch {}
        if (attempts >= 25) {
          clearInterval(poll);
          setGenerating(false);
          setGenMsg('⚠ Timed out — refresh in a minute');
        }
      }, 6000);
    } catch (e) {
      setGenerating(false);
      setGenMsg(`Error: ${e.message}`);
    }
  };

  const fmtDate = iso => {
    try { return new Date(iso).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}); }
    catch { return iso; }
  };

  // Action pill colours
  const ACTION_STYLE = {
    hold:  {bg:'rgba(26,74,170,.12)',  c:'#1a4aaa', label:'HOLD'},
    watch: {bg:'rgba(138,90,0,.12)',   c:'#8a5a00', label:'WATCH'},
    exit:  {bg:'rgba(136,30,16,.12)',  c:'#881e10', label:'EXIT'},
    add:   {bg:'rgba(26,106,26,.12)',  c:'#1a6a1a', label:'ADD'},
  };

  // Render fact text with clickable tickers
  const renderWithTickers = (text, tickers=[]) => {
    if (!tickers.length) return text;
    const pattern = new RegExp(`(${tickers.map(t=>t.replace(/[-]/g,'\\$&')).join('|')})`, 'g');
    return text.split(pattern).map((part,i) =>
      tickers.includes(part)
        ? <span key={i} className="tlink" style={{fontWeight:600}} onClick={()=>openPanel({name:part,sector:''})}>{part}</span>
        : part
    );
  };

  return (
    <div>
      {/* Header + Generate button */}
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',
        marginBottom:12,paddingBottom:8,borderBottom:'.5px solid var(--hair)'}}>
        <span style={{fontSize:11,fontWeight:700,letterSpacing:'.07em',textTransform:'uppercase',color:'var(--amber)'}}>
          💼 AI Trade Analysis
        </span>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {genMsg && (
            <span style={{fontSize:11,fontWeight:600,
              color:genMsg.startsWith('✓')?'var(--up-field-ink)':genMsg.startsWith('⚠')?'var(--amber-ink)':'var(--down-field-ink)'}}>
              {genMsg}
            </span>
          )}
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:4,
              border:'.5px solid var(--hair-strong)',background:'var(--surface-2)',color:'var(--ink-2)',
              cursor:generating?'default':'pointer',opacity:generating?0.6:1}}>
            {generating ? '⏳' : '⚡ Generate'}
          </button>
        </div>
      </div>
      {status === 'loading' && (
        <div style={{padding:'16px 0',color:'var(--ink-3)',fontSize:11}}>Loading trade analysis…</div>
      )}
      {status === 'empty' && !generating && (
        <div style={{padding:'16px 0',color:'var(--ink-3)',fontSize:11}}>
          No trade analysis yet — generates nightly at 00:30 IST when trades are open, or click Generate.
        </div>
      )}
      {days.map((day,di)=>(
        <div key={di} className="card" style={{marginBottom:7,overflow:'hidden'}}>
          {/* date in "7 Aug 2025" format */}
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',
            padding:'8px 12px',background:'var(--surface-2)',cursor:'pointer',userSelect:'none'}}
            onClick={()=>setExpanded(e=>({...e,[di]:!e[di]}))}>
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <span style={{fontWeight:700,fontSize:12,color:'var(--ink)'}}>{fmtDate(day.date)}</span>
              <span style={{fontSize:11,color:'var(--ink-3)'}}>{day.items.length} observations</span>
            </div>
            <span style={{fontSize:11,color:'var(--ink-3)',transform:expanded[di]?'rotate(180deg)':'none',transition:'transform .2s'}}>▼</span>
          </div>
          {expanded[di]&&(
            <div style={{padding:'0 12px'}}>
              {day.items.map((obs,i)=>{
                const as = ACTION_STYLE[obs.action] || ACTION_STYLE.hold;
                return (
                  <div key={i} style={{padding:'8px 0',borderBottom:i<day.items.length-1?'.5px solid var(--hair)':'none'}}>
                    {/* Fix #3: action pill inline as bullet on same row as fact */}
                    <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',gap:8}}>
                      <div style={{display:'flex',alignItems:'flex-start',gap:7,flex:1}}>
                        {/* Action pill — acts as bullet point */}
                        <span style={{
                          fontSize:11,fontWeight:700,padding:'2px 6px',borderRadius:3,flexShrink:0,marginTop:2,
                          background:as.bg,color:as.c,
                          border:`.5px solid ${as.c}33`,
                          textTransform:'uppercase',letterSpacing:'.05em',
                        }}>{as.label}</span>
                        <div>
                          <div style={{fontSize:11,color:'var(--ink)',lineHeight:1.55,marginBottom:3}}>
                            {renderWithTickers(obs.fact, obs.tickers)}
                          </div>
                          <div style={{fontSize:11,color:'var(--amber-ink)',lineHeight:1.5}}>
                            › {renderWithTickers(obs.rec, obs.tickers)}
                          </div>
                        </div>
                      </div>
                      <ObsFeedback obsId={`journal-ai-${day.date}-${i}`}/>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

/* ── Realized Trades ── */
/* pivot label helpers */
const TOPBAR_H = 44; // matches TopBar sticky height

function _rtMonthLabel(key) {
  const [y, m] = key.split('-');
  return new Date(+y, +m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
}
function _rtWeekLabel(monday) {
  const mon = new Date(monday + 'T00:00:00');
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const fmt = d => d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  return `${fmt(mon)} – ${fmt(sun)}`;
}
function _rtDayLabel(key) {
  const d = new Date(key + 'T00:00:00');
  return d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
}
let _rtView2 = 'trade';

function RealizedTradesTab({ subtabH = 40 }) {
  const { open: openPanel } = useSidePanel();
  const [logs, setLogs]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [view, setView]           = useState(_rtView2);
  const controlsRef = React.useRef(null);
  const [controlsH, setControlsH] = useState(80);
  const colHeaderRef = React.useRef(null);
  const [colHeaderH, setColHeaderH] = useState(30);
  const FILTER_DEFAULTS = { segment: 'All', strategy: 'All', underlying: '', acct: 'All', from: '', to: '' };
  const [filters, setFilters]     = useState(FILTER_DEFAULTS);
  const [sort, setSort]           = useState({ col: 'dateOpen', dir: 1 });
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [expandedWeeks,  setExpandedWeeks]  = useState(new Set());
  const [expandedDays,   setExpandedDays]   = useState(new Set());
  const [expandedGroups, setExpandedGroups] = useState({});
  const [registryStrategies, setRegistryStrategies] = useState([]);

  const handleView = v => { _rtView2 = v; setView(v); };

  useEffect(() => {
    const el = controlsRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setControlsH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const el = colHeaderRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setColHeaderH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    apiFetch('/api/strategies')
      .then(r => r.json())
      .then(d => setRegistryStrategies(d.strategies ?? []))
      .catch(() => {});
  }, []);

  const loadTrades = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res  = await apiFetch('/api/journal/trades?status=Closed');
      const data = await res.json();
      setLogs(data.trades ?? []);
    } catch {
      setError('Could not load trades.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTrades(); }, [loadTrades]);

  const journalStrategyNames = useMemo(() => [...new Set(logs.map(t => t.strategy).filter(Boolean))], [logs]);
  const strategies = useMemo(() => strategyFilterOptions(registryStrategies, journalStrategyNames), [registryStrategies, journalStrategyNames]);
  const accounts   = useMemo(() => {
    const fromJournal = logs.map(t => t.acct).filter(Boolean);
    return [...new Set([...ACCOUNTS_BASE, ...fromJournal])];
  }, [logs]);

  const filtered = useMemo(() => logs.filter(t => {
    if (filters.segment   !== 'All' && (t.segment || 'F&O') !== filters.segment) return false;
    if (filters.strategy  !== 'All' && t.strategy !== filters.strategy)           return false;
    if (filters.underlying && !t.underlying.toLowerCase().includes(filters.underlying.toLowerCase())) return false;
    if (filters.acct !== 'All' && t.acct !== filters.acct)                         return false;
    if (filters.from && (t.dateClose || '') < filters.from)                        return false;
    if (filters.to   && (t.dateClose || '') > filters.to)                          return false;
    return true;
  }), [logs, filters]);

  const sortTrades = useCallback(rows => [...rows].sort((a, b) => {
    const av = sort.col === 'pnl' ? (a.pnl ?? 0) : (a[sort.col] || '');
    const bv = sort.col === 'pnl' ? (b.pnl ?? 0) : (b[sort.col] || '');
    if (typeof av === 'string') return av.localeCompare(bv) * sort.dir;
    return (av - bv) * sort.dir;
  }), [sort]);

  const handleSort = col => setSort(s => s.col === col ? { col, dir: -s.dir } : { col, dir: -1 });

  // Build 3-level hierarchy: month → week (per-month) → day
  const hierarchy = useMemo(() => {
    const months = {};
    filtered.forEach(t => {
      const dc = t.dateClose;
      if (!dc) return;
      const monthKey = dc.slice(0, 7);
      const dayKey   = dc.slice(0, 10);
      const d = new Date(dc + 'T00:00:00');
      const dow = d.getDay() || 7;
      const mon = new Date(d); mon.setDate(d.getDate() - dow + 1);
      const weekMonday = mon.toISOString().slice(0, 10);
      const weekId = `${monthKey}|${weekMonday}`;

      if (!months[monthKey]) months[monthKey] = { key: monthKey, weeks: {}, trades: [] };
      months[monthKey].trades.push(t);
      if (!months[monthKey].weeks[weekId]) months[monthKey].weeks[weekId] = { id: weekId, weekMonday, days: {}, trades: [] };
      months[monthKey].weeks[weekId].trades.push(t);
      if (!months[monthKey].weeks[weekId].days[dayKey]) months[monthKey].weeks[weekId].days[dayKey] = { key: dayKey, trades: [] };
      months[monthKey].weeks[weekId].days[dayKey].trades.push(t);
    });
    return Object.values(months)
      .sort((a, b) => a.key.localeCompare(b.key))
      .map(m => ({
        ...m,
        weeks: Object.values(m.weeks)
          .sort((a, b) => a.weekMonday.localeCompare(b.weekMonday))
          .map(w => ({ ...w, days: Object.values(w.days).sort((a, b) => a.key.localeCompare(b.key)) })),
      }));
  }, [filtered]);

  const allMonthKeys = useMemo(() => hierarchy.map(m => m.key), [hierarchy]);
  const allWeekIds   = useMemo(() => hierarchy.flatMap(m => m.weeks.map(w => w.id)), [hierarchy]);

  const collapseAll  = () => { setExpandedMonths(new Set()); setExpandedWeeks(new Set()); setExpandedDays(new Set()); };
  const expandMonths = () => { setExpandedMonths(new Set(allMonthKeys)); setExpandedWeeks(new Set()); setExpandedDays(new Set()); };
  const expandWeeks  = () => { setExpandedMonths(new Set(allMonthKeys)); setExpandedWeeks(new Set(allWeekIds)); setExpandedDays(new Set()); };

  const sumPnl = trades => trades.reduce((s, t) => s + (t.pnl || 0), 0);

  const makeGroups = trades => {
    const gmap = {};
    sortTrades(trades).forEach(t => {
      const gid = t.groupId || t.utn;
      if (!gmap[gid]) gmap[gid] = {
        groupId: gid, underlying: t.underlying, strategy: t.strategy,
        segment: t.segment || 'F&O', acct: t.acct,
        dateOpen: t.dateOpen, dateClose: t.dateClose, legs: [], netPnl: 0,
      };
      gmap[gid].legs.push(t);
      gmap[gid].netPnl += (t.pnl || 0);
      if (!gmap[gid].dateOpen || t.dateOpen < gmap[gid].dateOpen) gmap[gid].dateOpen = t.dateOpen;
      if (t.dateClose && (!gmap[gid].dateClose || t.dateClose > gmap[gid].dateClose)) gmap[gid].dateClose = t.dateClose;
    });
    return Object.values(gmap);
  };

  // Per-day trade table (own thead + rows)
  const TradeTable = ({ trades }) => {
    const SortTH = ({ col, children, style = {} }) => {
      const active = sort.col === col;
      return (
        <th onClick={() => handleSort(col)} style={{ cursor: 'pointer', userSelect: 'none', ...style }}>
          {children}
          <span style={{ marginLeft: 2, opacity: active ? 1 : .3, fontSize: 11,
            color: active ? 'var(--amber-ink)' : 'var(--ink-3)' }}>
            {active ? (sort.dir > 0 ? '↑' : '↓') : '↕'}
          </span>
        </th>
      );
    };

    const tradeRows = sortTrades(trades).map((t, i) => {
      const tpp = (t.pnl ?? 0) >= 0;
      return (
        <tr key={i}>
          <td />
          <td className="thl" style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-3)' }}>{t.utn}</td>
          <td className="thl" style={{ fontWeight: 700, fontSize: 11, color: 'var(--amber-ink)', cursor: 'pointer' }}
            onClick={() => openPanel({ name: t.underlying, sector: '' })}>{t.underlying}</td>
          <td className="thl" style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-3)',
            maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.groupId}</td>
          <td className="thl" style={{ fontSize: 11, color: 'var(--ink-2)' }}>{t.strategy}</td>
          <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t.segment}</td>
          <td className="thl" style={{ fontSize: 11 }}>{t.inst}</td>
          <td style={{ textAlign: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
              background: t.bs === 'Buy' ? 'rgba(26,106,26,.1)' : 'rgba(136,30,16,.1)',
              color: t.bs === 'Buy' ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
              {t.bs === 'Buy' ? 'Long' : 'Short'}
            </span>
          </td>
          <td style={{ fontSize: 11 }}>{t.lots}</td>
          <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t.lotSize}</td>
          <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{t.acct}</td>
          <td className="thl" style={{ fontSize: 11 }}>{fmtD(t.dateOpen)}</td>
          <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{fmtD(t.dateClose)}</td>
          <td style={{ fontWeight: 600 }}>₹{t.entry}</td>
          <td style={{ fontSize: 11 }}>{t.exit ? `₹${t.exit}` : '—'}</td>
          <td style={{ fontWeight: 700, fontFamily: 'var(--font-data)',
            color: tpp ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
            {t.pnl != null ? `${tpp ? '+' : '−'}₹${Math.abs(t.pnl).toLocaleString()}` : '—'}
          </td>
          <NotesCell notes={t.notes} />
        </tr>
      );
    });

    const groupRows = makeGroups(trades).map((g, gi) => {
      const gpp = g.netPnl >= 0;
      const isGrpOpen = expandedGroups[g.groupId] === true;
      return (
        <React.Fragment key={gi}>
          <tr style={{ background: 'var(--surface-2)', cursor: 'pointer' }}
            onClick={() => setExpandedGroups(e => ({ ...e, [g.groupId]: !isGrpOpen }))}>
            <td style={{ textAlign: 'center', color: 'var(--ink-3)', fontSize: 11, fontWeight: 700 }}>
              {isGrpOpen ? '▾' : '▸'}
            </td>
            <td className="thl" />
            <td className="thl" style={{ fontWeight: 700, color: 'var(--amber-ink)', cursor: 'pointer' }}
              onClick={e => { e.stopPropagation(); openPanel({ name: g.underlying, sector: '' }); }}>
              {g.underlying}
            </td>
            <td className="thl" style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-3)',
              maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>{g.groupId}</td>
            <td className="thl" style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-2)' }}>{g.strategy}</td>
            <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{g.segment}</td>
            <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)', fontWeight: 600 }}>
              {g.legs.length} leg{g.legs.length > 1 ? 's' : ''}
            </td>
            <td /><td /><td />
            <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{g.acct}</td>
            <td className="thl" style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink)' }}>{fmtD(g.dateOpen)}</td>
            <td className="thl" style={{ fontSize: 11, color: 'var(--ink)' }}>{fmtD(g.dateClose)}</td>
            <td /><td />
            <td style={{ fontWeight: 700, fontSize: 11, fontFamily: 'var(--font-data)',
              color: gpp ? 'var(--up-field-ink)' : 'var(--down-field-ink)', whiteSpace: 'nowrap' }}>
              {gpp ? '+' : '−'}₹{Math.abs(g.netPnl).toLocaleString()}
            </td>
            <td className="thl" />
          </tr>
          {isGrpOpen && g.legs.map((leg, li) => {
            const lpp = (leg.pnl ?? 0) >= 0;
            return (
              <tr key={li} style={{ borderBottom: '.5px solid var(--hair)' }}>
                <td />
                <td className="thl" style={{ fontFamily: 'var(--font-data)', fontSize: 11, color: 'var(--ink-3)', paddingLeft: 8 }}>{leg.utn}</td>
                <td className="thl" /><td className="thl" /><td className="thl" /><td className="thl" />
                <td className="thl" style={{ paddingLeft: 16, fontSize: 11, color: 'var(--ink-2)' }}>
                  <span style={{ color: 'var(--ink-3)', marginRight: 4, fontSize: 11 }}>↳</span>{leg.inst || '—'}
                </td>
                <td style={{ textAlign: 'center' }}>
                  <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 5px', borderRadius: 3,
                    background: leg.bs === 'Buy' ? 'rgba(26,106,26,.1)' : 'rgba(136,30,16,.1)',
                    color: leg.bs === 'Buy' ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
                    {leg.bs === 'Buy' ? 'Long' : 'Short'}
                  </span>
                </td>
                <td style={{ fontSize: 11 }}>{leg.lots}</td>
                <td style={{ fontSize: 11, color: 'var(--ink-3)' }}>{leg.lotSize}</td>
                <td className="thl" />
                <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{fmtD(leg.dateOpen)}</td>
                <td className="thl" style={{ fontSize: 11, color: 'var(--ink-3)' }}>{fmtD(leg.dateClose)}</td>
                <td style={{ fontWeight: 600, fontSize: 11 }}>₹{leg.entry}</td>
                <td style={{ fontSize: 11 }}>{leg.exit ? `₹${leg.exit}` : '—'}</td>
                <td style={{ fontFamily: 'var(--font-data)', fontSize: 11, fontWeight: leg.pnl != null ? 700 : 400,
                  color: leg.pnl != null ? (lpp ? 'var(--up-field-ink)' : 'var(--down-field-ink)') : 'var(--ink-3)' }}>
                  {leg.pnl != null ? `${lpp ? '+' : '−'}₹${Math.abs(leg.pnl).toLocaleString()}` : '—'}
                </td>
                <NotesCell notes={leg.notes} />
              </tr>
            );
          })}
        </React.Fragment>
      );
    });

    return (
      <div style={{ overflowX: 'auto', borderTop: '.5px solid var(--hair)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, tableLayout: 'fixed' }}>
          <thead>
            <tr style={{ background: 'var(--surface-2)', borderBottom: '.5px solid var(--hair-strong)' }}>
              <th style={{ width: 20 }} />
              <SortTH col="utn"        style={{ textAlign: 'left', width: 90  }}>UTN</SortTH>
              <SortTH col="underlying" style={{ textAlign: 'left', width: 100 }}>Underlying</SortTH>
              <SortTH col="groupId"    style={{ textAlign: 'left', width: 140 }}>Group ID</SortTH>
              <SortTH col="strategy"   style={{ textAlign: 'left', width: 100 }}>Strategy</SortTH>
              <SortTH col="segment"    style={{ textAlign: 'left', width: 70  }}>Segment</SortTH>
              <th style={{ textAlign: 'left',   width: 150 }}>Instrument</th>
              <th style={{ textAlign: 'center', width: 54  }}>L/S</th>
              <th style={{ textAlign: 'right',  width: 60  }}>Lots/Qty</th>
              <th style={{ textAlign: 'right',  width: 50  }}>Lot Sz</th>
              <SortTH col="acct"       style={{ textAlign: 'left', width: 76  }}>Account</SortTH>
              <SortTH col="dateOpen"   style={{ textAlign: 'left', width: 80  }}>Open</SortTH>
              <SortTH col="dateClose"  style={{ textAlign: 'left', width: 80  }}>Close</SortTH>
              <th style={{ textAlign: 'right',  width: 64  }}>Entry ₹</th>
              <th style={{ textAlign: 'right',  width: 60  }}>Exit ₹</th>
              <SortTH col="pnl"        style={{ textAlign: 'right', width: 100 }}>P&L ₹</SortTH>
              <th style={{ textAlign: 'center', width: 28  }}>ℹ</th>
            </tr>
          </thead>
          <tbody>
            {view === 'trade' ? tradeRows : groupRows}
          </tbody>
        </table>
      </div>
    );
  };

  // Summary stats from filtered trades
  const totalTrades = filtered.length;
  const totalPnl    = sumPnl(filtered);
  const totalPp     = totalPnl >= 0;

  return (
    <div>
      {/* Controls + summary — sticky block */}
      <div ref={controlsRef} style={{ position: 'sticky', top: TOPBAR_H + subtabH, zIndex: 10,
        background: 'var(--paper)', paddingBottom: 6 }}>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', paddingBottom: 0 }}>
          <div className="seg">
            <button className={`sbtn${view === 'trade' ? ' on' : ''}`} onClick={() => handleView('trade')}>Trade View</button>
            <button className={`sbtn${view === 'group' ? ' on' : ''}`} onClick={() => handleView('group')}>Group View</button>
          </div>
          <div className="seg">
            <button className="sbtn" onClick={collapseAll}>Collapse All</button>
            <button className="sbtn" onClick={expandMonths}>↓ Months</button>
            <button className="sbtn" onClick={expandWeeks}>↓ Weeks</button>
          </div>
          <Select value={filters.segment} onChange={e => setFilters(f => ({ ...f, segment: e.target.value }))}>
            <option value="All">All Segments</option>
            {SEGMENTS.map(s => <option key={s}>{s}</option>)}
          </Select>
          <Select value={filters.strategy} onChange={e => setFilters(f => ({ ...f, strategy: e.target.value }))}>
            <option value="All">All Strategies</option>
            {strategies.map(s => <option key={s}>{s}</option>)}
          </Select>
          <input className="inp" placeholder="Underlying..." value={filters.underlying}
            onChange={e => setFilters(f => ({ ...f, underlying: e.target.value }))} style={{ minWidth: 100 }} />
          <Select value={filters.acct} onChange={e => setFilters(f => ({ ...f, acct: e.target.value }))}>
            <option value="All">All Accounts</option>
            {accounts.map(a => <option key={a}>{a}</option>)}
          </Select>
          <input className="inp" type="date" value={filters.from}
            onChange={e => setFilters(f => ({ ...f, from: e.target.value }))} style={{ width: 110 }} />
          <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>to</span>
          <input className="inp" type="date" value={filters.to}
            onChange={e => setFilters(f => ({ ...f, to: e.target.value }))} style={{ width: 110 }} />
          <button className="pill" style={{ marginLeft: 'auto' }} onClick={() => setFilters(FILTER_DEFAULTS)}>Reset</button>
          <button className="pill" onClick={loadTrades} style={{ display: 'flex', alignItems: 'center', gap: 4 }} title="Refresh">
            <RefreshIcon />
          </button>
        </div>
        {/* Summary — no box, plain text on surface */}
        {!loading && filtered.length > 0 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '5px 2px 0', fontSize: 11 }}>
            <span style={{ color: 'var(--ink-3)' }}>
              {Object.values(filters).some(v => v && v !== 'All')
                ? <><span style={{ fontWeight: 700, color: 'var(--ink)' }}>{totalTrades}</span> filtered closed trade{totalTrades !== 1 ? 's' : ''}</>
                : <><span style={{ fontWeight: 700, color: 'var(--ink)' }}>{totalTrades}</span> closed trade{totalTrades !== 1 ? 's' : ''}</>
              }
            </span>
            <span style={{ fontWeight: 700, fontFamily: 'var(--font-data)', fontSize: 12,
              color: totalPp ? 'var(--up-field-ink)' : 'var(--down-field-ink)' }}>
              Net P&L&nbsp;&nbsp;{totalPp ? '+' : '−'}₹{Math.abs(totalPnl).toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {error && (
        <div style={{ marginBottom: 8, padding: '7px 12px', background: 'rgba(136,30,16,.08)',
          border: '.5px solid rgba(136,30,16,.3)', borderRadius: 6, fontSize: 11, color: 'var(--down-field-ink)' }}>
          {error}
        </div>
      )}
      {loading && (
        <div style={{ padding: '24px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 11 }}>⏳ Loading…</div>
      )}
      {!loading && hierarchy.length === 0 && (
        <div style={{ padding: '32px', textAlign: 'center', color: 'var(--ink-3)', fontSize: 11 }}>
          No closed trades found{Object.values(filters).some(v => v && v !== 'All') ? ' matching these filters' : ''}.
        </div>
      )}

      {/* Hierarchy table */}
      {!loading && hierarchy.length > 0 && (
        <div style={{ border: '.5px solid var(--hair-strong)', borderRadius: 5 }}>
          {/* Column header — sticky below controls block */}
          <div ref={colHeaderRef} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 140px',
            padding: '6px 14px', background: 'var(--surface-2)',
            borderBottom: '.5px solid var(--hair-strong)', userSelect: 'none',
            position: 'sticky', top: TOPBAR_H + subtabH + controlsH, zIndex: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber-ink)', textTransform: 'uppercase', letterSpacing: '.08em' }}>Period</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber-ink)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right' }}>Trades</span>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--amber-ink)', textTransform: 'uppercase', letterSpacing: '.08em', textAlign: 'right' }}>P&L</span>
          </div>

          {hierarchy.map(month => {
            const mExp = expandedMonths.has(month.key);
            const mPnl = sumPnl(month.trades);
            const mpp  = mPnl >= 0;
            const [mName, mYear] = _rtMonthLabel(month.key).split(' ');
            return (
              <div key={month.key}>
                {/* Month row — sticky below column header */}
                <div onClick={() => setExpandedMonths(s => { const n = new Set(s); n.has(month.key) ? n.delete(month.key) : n.add(month.key); return n; })}
                  style={{ display: 'grid', gridTemplateColumns: '1fr 80px 140px',
                    padding: '10px 14px', background: 'var(--surface-2)',
                    borderTop: '2px solid var(--hair-strong)', cursor: 'pointer',
                    position: 'sticky', top: TOPBAR_H + subtabH + controlsH + colHeaderH - 1, zIndex: 3 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 11, color: 'var(--ink-3)', width: 14, flexShrink: 0, fontFamily: 'var(--font-data)' }}>
                      {mExp ? '∨' : '›'}
                    </span>
                    <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2,
                      background: mpp ? 'var(--up-field-ink)' : 'var(--down-field-ink)', flexShrink: 0, marginRight: 2 }} />
                    <span style={{ fontSize: 16, fontWeight: 700, fontFamily: 'Georgia, serif', color: 'var(--ink)', letterSpacing: '-.01em' }}>
                      {mName}
                    </span>
                    <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--ink-3)', marginLeft: 2 }}>{mYear}</span>
                  </span>
                  <span style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'right', alignSelf: 'center', fontFamily: 'var(--font-data)' }}>
                    {month.trades.length}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--font-data)', textAlign: 'right',
                    color: mpp ? 'var(--up-field-ink)' : 'var(--down-field-ink)', alignSelf: 'center' }}>
                    {mpp ? '+' : '−'}₹{Math.abs(mPnl).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>

                {mExp && month.weeks.map(week => {
                  const wExp = expandedWeeks.has(week.id);
                  const wPnl = sumPnl(week.trades);
                  const wpp  = wPnl >= 0;
                  return (
                    <div key={week.id}>
                      {/* Week row */}
                      <div onClick={() => setExpandedWeeks(s => { const n = new Set(s); n.has(week.id) ? n.delete(week.id) : n.add(week.id); return n; })}
                        style={{ display: 'grid', gridTemplateColumns: '1fr 80px 140px',
                          padding: '7px 14px', borderTop: '.5px solid var(--hair)',
                          background: 'var(--surface)', cursor: 'pointer' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 22 }}>
                          <span style={{ fontSize: 11, color: 'var(--ink-3)', width: 14, flexShrink: 0, fontFamily: 'var(--font-data)' }}>
                            {wExp ? '∨' : '›'}
                          </span>
                          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink-2)' }}>{_rtWeekLabel(week.weekMonday)}</span>
                        </span>
                        <span style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'right', alignSelf: 'center', fontFamily: 'var(--font-data)' }}>
                          {week.trades.length}
                        </span>
                        <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-data)', textAlign: 'right',
                          color: wpp ? 'var(--up-field-ink)' : 'var(--down-field-ink)', alignSelf: 'center' }}>
                          {wpp ? '+' : '−'}₹{Math.abs(wPnl).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>

                      {wExp && week.days.map(day => {
                        const dExp = expandedDays.has(day.key);
                        const dPnl = sumPnl(day.trades);
                        const dpp  = dPnl >= 0;
                        return (
                          <div key={day.key}>
                            {/* Day row */}
                            <div onClick={() => setExpandedDays(s => { const n = new Set(s); n.has(day.key) ? n.delete(day.key) : n.add(day.key); return n; })}
                              style={{ display: 'grid', gridTemplateColumns: '1fr 80px 140px',
                                padding: '6px 14px', borderTop: '.5px solid var(--hair)',
                                background: 'rgba(0,0,0,.018)', cursor: 'pointer' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 44 }}>
                                <span style={{ fontSize: 11, color: 'var(--ink-3)', width: 14, flexShrink: 0, fontFamily: 'var(--font-data)' }}>
                                  {dExp ? '∨' : '›'}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--ink-3)' }}>{_rtDayLabel(day.key)}</span>
                              </span>
                              <span style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'right', alignSelf: 'center', fontFamily: 'var(--font-data)' }}>
                                {day.trades.length}
                              </span>
                              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: 'var(--font-data)', textAlign: 'right',
                                color: dpp ? 'var(--up-field-ink)' : 'var(--down-field-ink)', alignSelf: 'center' }}>
                                {dpp ? '+' : '−'}₹{Math.abs(dPnl).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>

                            {/* Per-day trade table — sunken well */}
                            {dExp && (
                              <div style={{ borderTop: '.5px solid var(--hair)',
                                background: 'rgba(0,0,0,.03)',
                                borderLeft: '3px solid var(--amber-ink)' }}>
                                <TradeTable trades={day.trades} />
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Main ── */
const TABS = [
  {v:'open',      l:'Open Positions'},
  {v:'journal',   l:'Trade Journal'},
  {v:'realized',  l:'Realized Trades'},
  {v:'analytics', l:'Analytics'},
  {v:'ai',        l:'AI Summary'},
];

export default function JournalTab({ initSub='', onSubChange }) {
  const VALID_SUBS = ['open','journal','realized','analytics','ai'];
  const [sub, setSub] = useState(() => VALID_SUBS.includes(initSub) ? initSub : 'open');
  const [journalInitFilters, setJournalInitFilters] = useState(null);
  const subtabRef = React.useRef(null);
  const [subtabH, setSubtabH] = useState(40);

  const handleSub = s => { setSub(s); onSubChange && onSubChange(s); };

  const handleDrilldown = (filters) => {
    setJournalInitFilters(filters);
    setSub('journal');
    onSubChange && onSubChange('journal');
  };

  useEffect(() => {
    if (initSub && VALID_SUBS.includes(initSub)) setSub(initSub);
  }, [initSub]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const el = subtabRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSubtabH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      <div ref={subtabRef} style={{ position: 'sticky', top: TOPBAR_H, zIndex: 20, background: 'var(--paper)', overflow: 'hidden' }}>
        <SubTabs tabs={TABS} val={sub} onChange={handleSub}/>
      </div>
      {sub==='open'      && <OpenPositionsTab goToJournal={() => handleSub('journal')}/>}
      {sub==='journal'   && <TradeJournalTab initialFilters={journalInitFilters} subtabH={subtabH}/>}
      {sub==='realized'  && <RealizedTradesTab subtabH={subtabH}/>}
      {sub==='analytics' && <AnalyticsTab onDrilldown={handleDrilldown} subtabH={subtabH}/>}
      {sub==='ai'        && <AITradeTab/>}
    </div>
  );
}
